import {Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {combineLatest, firstValueFrom, Observable, Subject} from 'rxjs';
import {debounceTime, filter, take, takeUntil} from 'rxjs/operators';
import {
  selectAllLineColours,
  selectAllStockItemsForCurrentSelectedStore,
  selectAllStoreDepartments,
  selectAllStoreSuppliers,
  selectCreateNewStockItemSubmit,
  selectDepAndSubDepForUserSelectedStore,
  selectLineColours,
  selectStoreSettingsForSelectedUserStore,
  selectSuppliersForUserSelectedStore,
} from '../../store/stock-manager.selectors';
import {
  createNewStockItem,
  getStoreDepartments,
  getStoreDepartmentsForStockManager,
  getStoreLineColours,
  getStoreSuppliers,
  navigateToStoreSettings,
  resetCreateNewStockItemSubmit,
} from '../../store/stock-manager.actions';
import {IDepartment, ISubDepartment} from '../../../../shared/shared-models/stock/departments';
import {ISharedStoreSettings} from '../../../../shared/shared-models/firebase/shared-store-settings';
import {
  sItemIntToKey,
  StockItem,
  StockItemUpdateFieldValue,
  StockItemUpdateObj,
  Supplier,
  VatRates,
} from '../../../../shared-utilities/models-old/datastructures';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {
  selectAllSharedGridVatRates,
  selectPriceBandingForUserSelectedStore,
  selectSharedGridVatRateBySelectedStore,
} from '../../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {getVatRateConversion} from '../../../../shared-modules/shared-grid/store/shared-grid.actions';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {calculatePriceFromGpValue} from '../../../../shared/shared-utils/calculations/calculations-stock';
import {applyPriceBanding,} from '../../../../shared/shared-utils/price/price-banding.utils';
import {IPriceBand} from '../../../../shared/shared-models/price-banding/price-band';
import {IStore} from '../../../../shared/shared-models/store/store';
import {settingStoreStockItemAddRule} from '../../../settings-new/store/setting.actions';
import {IAddItemRule} from '../../../settings-new/models';
import {
  selectAllStoresAddItemRules,
  selectIsAddItemRulesLoading,
  selectSelectedStoreAddItemRule,
  selectSelectedStoreStockTags,
} from '../../../settings-new/store/setting.selectors';
import {selectSelectedStore, selectStores} from '../../../user-access/user-stores-store/user-store.selectors';
import {setSelectedStore} from '../../../user-access/user-stores-store/user-store.actions';
import {getDeepCopyOfObject, isNonEmptyObject} from '../../../../shared/shared-utils/object/object.utils';
import {
  GallixApiService,
  INextStockCodesResponse,
  IStoreStockCodeRule,
} from '../../../../shared/shared-services/big-query/gallix-api-service.service';
import {DropdownKey, ILineColorOption, ISupplierOption, IVatRateOption, PerStoreField} from '../../models/add-stock-item-interfaces';
import {ISearchableFields} from 'src/app/shared/shared-models/type-sense/default-searchable-fields';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {
  SharedModalBasicComponent,
} from '../../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {selectSelectedStockViewChanges} from '../../../notifications/store/notification.selectors';
import {DEFAULT_LOADER_FLASH, ISharedLoadingG} from '../../../../shared/shared-module-ui/models/animations';
import {NotificationResponseTypeEnum, INotification} from '../../../notifications/models';
import {updateNotification} from '../../../notifications/store/notification.actions';
import {
  calculateExclusivePrice,
  createStockItemFromForm,
  objectToSupplierOptions,
  parseVatRateId,
  updatePricesFromExclusive,
  updatePricesFromInclusive,
} from './add-stock-item.utils';
import {TLineColour} from '../../../settings-new/models';
import {
  decodeOnHoldCode,
  encodeOnHoldCode,
} from '../../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';
import {_ON_HOLD_TYPES} from '../../../settings-new/models/on-hold-code/on-hold-code.interface';
import {Ascii} from '../../../../shared/shared-models/global/ascii.enum';
import {
  StockManagerStockCodeAvailabilityService,
} from '../../services/stock-manager-stock-code-availability.service';
import {
  IStockManagerStockCodeAvailability,
  IStockManagerStockCodeConflict,
  TStockCodeCheckState,
} from '../../models/stock-manager-stock-code-availability.model';
import {
  StockManagerBarcodeAvailabilityService,
} from '../../services/stock-manager-barcode-availability.service';
import {
  IStockManagerBarcodeAvailability,
  IStockManagerBarcodeStoreConflict,
} from '../../models/stock-manager-barcode-availability.model';

@Component({
  selector: 'app-add-stock-item',
  templateUrl: './add-stock-item.component.html',
  styleUrls: ['./add-stock-item.component.scss'],
  standalone: false,
})
export class AddStockItemComponent implements OnInit, OnDestroy {

  //region ↓ Variable Declarations

  //#region --> Inputs
  @Input() retryAddStockItem = false;
  @Input() reason = '';
  @Input() retryNotification: INotification | null = null;
  //#endregion

  //region --> ViewChild Declarations
  @ViewChild('supplierInput') supplierInput!: ElementRef<HTMLInputElement>;
  @ViewChild('storeInput') storeInput!: ElementRef<HTMLInputElement>;
  @ViewChild('departmentInput') departmentInput!: ElementRef<HTMLInputElement>;
  @ViewChild('subDepartmentInput') subDepartmentInput!: ElementRef<HTMLInputElement>;
  @ViewChild('lineColorInput') lineColorInput!: ElementRef<HTMLInputElement>;
  @ViewChild('onHoldCodeInput') onHoldCodeInput!: ElementRef<HTMLInputElement>;
  @ViewChild('vatRateInput') vatRateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('multiSupplierInput') multiSupplierInput!: ElementRef<HTMLInputElement>;
  @ViewChild('tagsInput') tagsInput!: ElementRef<HTMLInputElement>;
  @ViewChild('barcodesInput') barcodesInput!: ElementRef<HTMLInputElement>;
  //endregion

  //region --> FormData
  isGettingItemCode = false;
  addItemSettings!: IAddItemRule;
  /**
   * Generated stock codes keyed by storeId. Populated by `onGenerateStockCodeClick` and
   * cleared whenever the inputs that affect generation change (dep / subDep / selected stores).
   */
  generatedCodesByStore: Record<string, string> = {};

  //#region --> Manual stock code entry
  /** Result of the last availability check for the manually typed stock code. */
  stockCodeCheckState: TStockCodeCheckState = 'unchecked';
  /** Stores that already use the checked stock code. Only populated when `stockCodeCheckState` is `taken`. */
  stockCodeConflicts: IStockManagerStockCodeConflict[] = [];
  /** The code that was checked, so the result can be invalidated when the user edits the field. */
  private checkedStockCode = '';
  /** True while the manual code entry default has not yet been applied from store settings. */
  private hasAppliedManualCodeEntryDefault = false;
  //#endregion

  //#region --> Barcode availability
  /** Result of the last barcode availability check across the selected stores. */
  barcodeCheckState: TStockCodeCheckState = 'unchecked';
  /** Stores that already use one of the entered barcodes. */
  barcodeConflicts: IStockManagerBarcodeStoreConflict[] = [];
  /** The barcode set that was checked, so the result is invalidated when the list changes. */
  private checkedBarcodeKey = '';
  //#endregion

  /** True from the moment Add Item is clicked until the Firestore write resolves. */
  isSubmitting = false;

  //#region --> Per-store (multi-store) field selection: Supplier / Line Colour / VAT Rate / Department
  /** Which per-store field modal is currently open (null = no modal). */
  activePerStoreField: PerStoreField | null = null;
  /** Loading flag while fetching each selected store's options. */
  isLoadingPerStoreData = false;
  /** The store row whose dropdown is currently expanded inside the modal. */
  openPerStoreDropdownStoreId: string | null = null;
  /** Per-store search terms inside the open modal, keyed by storeId. */
  perStoreSearchTerm: Record<string, string> = {};

  /** Option lists fetched per selected store. */
  suppliersByStore: Record<string, ISupplierOption[]> = {};
  lineColoursByStore: Record<string, ILineColorOption[]> = {};
  vatRatesByStore: Record<string, IVatRateOption[]> = {};
  departmentsByStore: Record<string, IDepartment[]> = {};

  /** The chosen value per store (null = intentionally blank). */
  selectedSupplierByStore: Record<string, ISupplierOption | null> = {};
  selectedLineColourByStore: Record<string, ILineColorOption | null> = {};
  selectedVatRateByStore: Record<string, IVatRateOption | null> = {};
  selectedDepartmentByStore: Record<string, IDepartment | null> = {};
  selectedSubDepartmentByStore: Record<string, ISubDepartment | null> = {};
  /** Additional (multiple) suppliers chosen per store. Multi-select, so an array per store. */
  selectedMultiSuppliersByStore: Record<string, ISupplierOption[]> = {};

  private allVatRatesMap: Record<string, VatRates> = {};
  /** Latest store-state snapshots so per-store options can be read without fetching in the component. */
  private allStoreSuppliersMap: Record<string, ISearchableFields> = {};
  private allLineColoursMap: Record<string, TLineColour> = {};
  private allStoreDepartmentsMap: Record<string, IDepartment[]> = {};
  //#endregion

  isDepartmentsLoading = true;
  isSubDepartmentsLoading = true;
  //region --> Dropdown visibility and positions
  showStoreDropdown = false;
  showDepartmentDropdown = false;
  //endregion
  showSubDepartmentDropdown = false;
  showSupplierDropdown = false;
  showOnHoldCodeDropdown = false;
  showMultiSupplierDropdown = false;
  showLineColorDropdown = false;
  showVatRateDropdown = false;
  showTagsDropdown = false;
  showBarcodesDropdown = false;
  dropdownPositions: Record<string, 'above' | 'below'> = {
    barcodes: 'below',
    department: 'below',
    lineColor: 'below',
    multiSupplier: 'below',
    onHoldCode: 'below',
    store: 'below',
    subDepartment: 'below',
    supplier: 'below',
    tags: 'below',
    vatRate: 'below',
  };

  //region --> Store data
  storeSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  stores: IStore[] = [];
  filteredStores: IStore[] = [];
  selectedStoresList: IStore[] = [];
  tempSelectedStores: IStore[] = [];
  disabledStoreIds: Set<string> = new Set<string>();
  private allAddItemSettingsMap: Record<string, IAddItemRule | undefined> = {};
  private hasShownMissingSettingsWarning = false;
  private hasDispatchedAllStoresFetch = false;

  //region --> Department data
  departmentSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  departments: IDepartment[] = [];
  filteredDepartments: IDepartment[] = [];
  //region --> Sub-department data
  subDepartmentSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  subDepartments: ISubDepartment[] = [];
  filteredSubDepartments: ISubDepartment[] = [];
  //#region --> Supplier data
  supplierSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  suppliers: ISupplierOption[] = [];
  filteredSuppliers: ISupplierOption[] = [];
  //region --> Multi Supplier data
  multiSupplierSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  selectedSuppliers: string[] = [];
  filteredMultiSuppliers: ISupplierOption[] = [];
  //region --> Barcode data
  barcodesSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  barcodeOptions: Record<string, ISelectOption> = {};
  //endregion
  selectedBarcodeKeys: string[] = [];
  tempSelectedBarcodes: string[] = [];
  newBarcodeInput = '';
  //region --> Line color data
  lineColorSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  lineColors: ILineColorOption[] = [];
  filteredLineColors: ILineColorOption[] = [];
  //region --> VAT rate data
  vatRateSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  vatRates: IVatRateOption[] = [];
  filteredVatRates: IVatRateOption[] = [];
  vatRatesObject!: VatRates;
  //region --> On Hold Code data
  onHoldCodeSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  //endregion
  onHoldTypes = {..._ON_HOLD_TYPES};
  selectedOnHoldValues: string[] = [];

  //region --> Tags data
  tagsSearchControl: FormControl<string | null> = new FormControl<string | null>('');
  tagsOptions: Record<string, ISelectOption> = {};
  selectedTags: string[] = [];
  tempSelectedTags: string[] = [];
  filteredTags: { name: string; global: boolean }[] = [];
  allTags: { name: string; global: boolean }[] = [];
  //endregion
  //region --> Store settings
  storeSettings!: ISharedStoreSettings;
  //endregion
  selectedStore!: IStore;
  priceBands: IPriceBand[] = [];
  isRetryDataLoading = false;
  retryDataLoadFailed = false;
  allStockItems: StockItem[] = [];
  //region --> Observables$
  departments$!: Observable<IDepartment[]>;
  //endregion
  storeSettings$!: Observable<ISharedStoreSettings>;
  lineColours$!: Observable<TLineColour>;
  suppliers$!: Observable<Record<string, Supplier> | ISearchableFields>;
  selectedStore$!: Observable<IStore>;
  stores$!: Observable<IStore[]>;
  priceBands$!: Observable<IPriceBand[]>;
  vatRate$!: Observable<VatRates>;
  addItemSettings$!: Observable<IAddItemRule>;
  allStockItems$!: Observable<StockItem[]>;
  protected readonly animation: Readonly<ISharedLoadingG> = DEFAULT_LOADER_FLASH;
  //endregion
  //region --> Form field navigation order
  private readonly fieldNavigationOrder: Record<string, string[]> = {
    basic: ['dep', 'subDep', 'desc', 'alternateDescription', 'barcode', 'lineColour', 'onHoldCode'],
    ordering: ['packSize', 'ordLvl', 'minOrderLevel', 'maxOrderLevel'],
    pricing: ['latestCost', 'vatRate', 'recommendedGP', 'nominalGP', 'sellPriIncl1', 'sellPriExcl1'],
    supplier: ['regularSuppCode', 'suppCode', 'supplierCodes'],
  };

  private readonly subGroupOrder: string[] = ['basic', 'pricing', 'ordering', 'supplier'];
  private form!: FormGroup;
  //endregion

  //region --> subscription management
  private readonly destroy$ = new Subject<boolean>();
  //endregion

  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;
  //endregion

  //region Constructor
  public constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly modalController: ModalControllerService,
    private readonly gallixApiService: GallixApiService,
    private readonly stockCodeAvailabilityService: StockManagerStockCodeAvailabilityService,
    private readonly barcodeAvailabilityService: StockManagerBarcodeAvailabilityService,
  ) {
  }

  //endregion

  //region Getters


  get getForm(): FormGroup {
    return this.form;
  }

  /** True when more than one store is selected — drives the per-store field UI. */
  get isMultiStore(): boolean {
    return this.selectedStoresList.length > 1;
  }

  /** True when the user has opted to type the stock code instead of generating it. */
  get isManualCodeEntry(): boolean {
    return !!this.form.get('basic.manualCodeEntry')?.value;
  }

  get manualStockCode(): string {
    return ((this.form.get('basic.code')?.value as string) || '').trim();
  }

  /** Stable key for the current barcode list, used to invalidate a stale check result. */
  private get barcodeCheckKey(): string {
    return this.selectedBarcodeKeys.join('|');
  }

  public get getButtonToolTip(): string {
    const hasDepartment = this.hasRequiredDepartmentForCodeGen();
    const hasSubDepartment = this.hasRequiredSubDepartmentForCodeGen();
    const hasStore = this.selectedStoresList.length > 0;

    const missing: string[] = [];
    if (!hasStore) missing.push('at least one store');
    if (!hasDepartment) missing.push('a department');
    if (!hasSubDepartment) missing.push('a sub-department');

    if (missing.length === 0) return '';
    return `Please select ${missing.join(' and ')}`;
  }

  public get perStoreModalTitle(): string {
    switch (this.activePerStoreField) {
      case 'supplier':
        return 'Select Supplier Per Store';
      case 'lineColour':
        return 'Select Line Colour Per Store';
      case 'vatRate':
        return 'Select VAT Rate Per Store';
      case 'multiSupplier':
        return 'Select Multiple Suppliers Per Store';
      case 'department':
        return 'Select Department Per Store';
      case 'subDepartment':
        return 'Select Sub-Department Per Store';
      default: {
        const _exhaustive: never = this.activePerStoreField;
        return _exhaustive;
      }
    }
  }

  public hasRequiredDepartmentForCodeGen(): boolean {
    if (this.isMultiStore) {
      return this.selectedStoresList.every((s: IStore) => !!this.selectedDepartmentByStore[s.storeId]);
    }
    return !!this.form.get('basic.dep')?.value;
  }

  public hasRequiredSubDepartmentForCodeGen(): boolean {
    if (this.isMultiStore) {
      return this.selectedStoresList.every((s: IStore) => !!this.selectedSubDepartmentByStore[s.storeId]);
    }
    return !!this.form.get('basic.subDep')?.value;
  }


  //endregion

  //#region Barcode Validation

  private checkForDuplicateBarcodes(barcodesToCheck: string[]): {
    hasDuplicates: boolean;
    duplicates: { barcode: string; stockCode: string; stockDescription: string }[];
  } {
    const duplicates: { barcode: string; stockCode: string; stockDescription: string }[] = [];

    for (const barcodeToCheck of barcodesToCheck) {
      if (!barcodeToCheck || barcodeToCheck.trim() === '') continue;

      for (const stockItem of this.allStockItems) {
        if (!stockItem) continue;

        const primaryBarcode = stockItem.barcode?.toString().trim();
        const multipleBarcodes = Array.isArray(stockItem.multipleBarcodes)
          ? stockItem.multipleBarcodes.map((b: string | number) => b.toString().trim())
          : [];

        const allBarcodes = [primaryBarcode, ...multipleBarcodes].filter(Boolean);

        if (allBarcodes.includes(barcodeToCheck.trim())) {
          duplicates.push({
            barcode: barcodeToCheck,
            stockCode: stockItem.code?.toString() || 'Unknown',
            stockDescription: stockItem.desc?.toString() || 'No description',
          });
          break;
        }
      }
    }

    return {
      hasDuplicates: duplicates.length > 0,
      duplicates,
    };
  }

  private async showDuplicateBarcodeError(duplicates: { barcode: string; stockCode: string; stockDescription: string }[]): Promise<void> {
    const duplicateList = duplicates
      .map((dup) => `• Barcode "${dup.barcode}" already exists in stock item "${dup.stockCode}" (${dup.stockDescription})`)
      .join('\n');

    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Duplicate Barcode Detected',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Cannot Add Stock Item',
      contextDescription: [
        'The following barcode(s) already exist in your stock file:',
        duplicateList,
        'Please use unique barcodes or remove the duplicate barcodes before saving.',
      ],
    };

    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });

    await modalController.present();
    await modalController.onDidDismiss();
  }

  //endregion

  //#region Lifecycle Hooks
  public ngOnInit(): void {
    this.initForm();
    this.subscribeToStore();
    this.setupDropdownSearchControls();
    this.setupPriceCalculations();
  }

  //endregion

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  //#region Initialization Methods
  public initForm(): void {
    this.form = this.fb.group({
      basic: this.fb.group({
        alternateDescription: ['', Validators.maxLength(150)],
        code: ['', Validators.required],
        dep: ['', Validators.required],
        desc: ['', [Validators.required, Validators.maxLength(150)]],
        lineColour: [''],
        manualCodeEntry: [false],
        onHoldCode: [''],
        subDep: ['', Validators.required],
      }),
      ordering: this.fb.group({
        maxOrderLevel: [0, Validators.min(0)],
        minOrderLevel: [0, Validators.min(0)],
        ordLvl: [0, Validators.min(0)],
        packSize: [1, Validators.min(1)],
      }),
      pricing: this.fb.group({
        latestCost: [null, [Validators.required, Validators.min(0)]],
        nominalGP: [null, [Validators.min(0), Validators.max(99.99)]],
        recommendedGP: [0, [Validators.min(0), Validators.max(99.99)]],
        sellPriExcl1: [null, [Validators.required, Validators.min(0)]],
        sellPriIncl1: [null, [Validators.required, Validators.min(0)]],
        vatRate: [null, Validators.required],
      }),
      supplier: this.fb.group({
        multipleSuppliers: [false],
        regularSuppCode: ['', Validators.required],
        suppCode: [''],
        supplierCodes: [[]],
      }),
    });
  }

  //endregion

  public subscribeToStore(): void {
    this.storeSettings$ = this.store.select(selectStoreSettingsForSelectedUserStore);
    this.storeSettings$.pipe(takeUntil(this.destroy$)).subscribe((storeSettings: ISharedStoreSettings): void => {
      this.storeSettings = storeSettings;

    });

    // Check add-item settings for every user store: bulk-load all rules once, then compute
    // which stores are missing settings (to disable them in the dropdown) and warn once.
    combineLatest([
      this.store.select(selectStores),
      this.store.select(selectAllStoresAddItemRules),
      this.store.select(selectIsAddItemRulesLoading),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([stores, allSettings, isLoading]: [IStore[], Record<string, IAddItemRule>, boolean]): void => {
        if (!stores || stores.length === 0) return;
        this.allAddItemSettingsMap = allSettings || {};

        if (!this.hasDispatchedAllStoresFetch) {
          this.hasDispatchedAllStoresFetch = true;
          this.store.dispatch(settingStoreStockItemAddRule.getAddItemRules({params: {storeAction: 'All'}}));
          return;
        }

        // Wait for the bulk fetch to finish before computing disabled stores / showing the warning.
        // Without this gate we'd flash all stores as "missing" before any rule arrives.
        if (isLoading) return;

        const missing = stores.filter((s: IStore) => !this.hasValidAddItemSettings(this.allAddItemSettingsMap[s.storeId]));
        this.disabledStoreIds = new Set(missing.map((s: IStore) => s.storeId));

        if (this.selectedStoresList.some((s: IStore) => this.disabledStoreIds.has(s.storeId))) {
          this.selectedStoresList = this.selectedStoresList.filter((s: IStore) => !this.disabledStoreIds.has(s.storeId));
        }
        if (this.tempSelectedStores.some((s: IStore) => this.disabledStoreIds.has(s.storeId))) {
          this.tempSelectedStores = this.tempSelectedStores.filter((s: IStore) => !this.disabledStoreIds.has(s.storeId));
        }

        // If the current global primary is disabled, switch to the first enabled store so
        // the form's option lists (departments, suppliers, etc.) load against a valid store.
        if (this.selectedStore?.storeId && this.disabledStoreIds.has(this.selectedStore.storeId)) {
          const fallback = stores.find((s: IStore) => !this.disabledStoreIds.has(s.storeId));
          if (fallback) {
            this.selectedStoresList = [
              fallback,
              ...this.selectedStoresList.filter((s: IStore) => s.storeId !== fallback.storeId),
            ];
            this.store.dispatch(setSelectedStore({selectedStore: fallback}));
          }
        }

        if (missing.length > 0 && !this.hasShownMissingSettingsWarning) {
          this.hasShownMissingSettingsWarning = true;
          void this.showStoresMissingSettingsWarning(missing);
        }
      });

    this.addItemSettings$ = this.store.select(selectSelectedStoreAddItemRule);
    this.addItemSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (addItemSettings: IAddItemRule): Promise<void> => {
        this.applyManualCodeEntryDefault(addItemSettings);
        if (this.hasValidAddItemSettings(addItemSettings)) {
          this.addItemSettings = addItemSettings;
          return;
        }

        // The current primary store has no valid settings. If any other store does,
        // suppress the navigate-to-settings prompt — the per-store warning will inform the user
        // and the disabled-store logic above will swap to a valid primary.
        const anyHasSettings = Object.values(this.allAddItemSettingsMap || {})
          .some((s: IAddItemRule | undefined) => this.hasValidAddItemSettings(s));
        if (anyHasSettings) {
          this.addItemSettings = addItemSettings;
          return;
        }

        if (!addItemSettings || !isNonEmptyObject(addItemSettings)) {
          const componentProps: ISharedModalBasic = {
            buttonAccept: false,
            buttonAcceptText: 'Confirm',
            buttonClose: true,
            buttonCloseCross: false,
            buttonCloseText: 'Close',
            modalTitle: 'No Add Stock Item Settings Found',
            modalTitleIcon: IconsEnum.settings,
            modalTitleIconColor: IconColourEnum.red,
            contextHeading: 'Please Complete Settings',
            contextDescription: [
              'Please contact your store admin or go to the settings page to complete the add stock item settings.',
              'Once The Settings Are complete you may add stock items.',
              'If you are an admin you will be automatically navigated to store settings.',
            ],
          };

          const modalController = await this.modalController.create({
            component: SharedModalBasicComponent,
            componentProps,
            cssClass: ['shared-basic-modal-css-medium'],
            backdropDismiss: false,
          });

          await modalController.present();
          await modalController.onDidDismiss();
          this.destroy$.next(true);
          await this.onModalClose();
          this.store.dispatch(navigateToStoreSettings());
        }
        this.addItemSettings = addItemSettings;
      });

    this.departments$ = this.store.select(selectDepAndSubDepForUserSelectedStore);
    this.departments$.pipe(takeUntil(this.destroy$)).subscribe((departments: IDepartment[]): void => {
      if (!departments || departments.length === 0) {
        this.isDepartmentsLoading = true;
        this.isSubDepartmentsLoading = true;
        this.store.dispatch(getStoreDepartmentsForStockManager());
        return;
      }
      this.departments = departments;
      this.filteredDepartments = [...this.departments];
      this.subDepartments = this.departments.flatMap((dep: IDepartment) => (dep.subDepartments ? Object.values(dep.subDepartments) : []));
      this.filteredSubDepartments = [...this.subDepartments];
      this.isDepartmentsLoading = false;
      this.isSubDepartmentsLoading = false;
    });

    this.suppliers$ = this.store.select(selectSuppliersForUserSelectedStore);
    this.suppliers$.pipe(takeUntil(this.destroy$)).subscribe((suppliers: Record<string, Supplier> | ISearchableFields): void => {
      if (suppliers) {
        this.suppliers = objectToSupplierOptions(suppliers);
        this.filteredSuppliers = [...this.suppliers];
        this.filteredMultiSuppliers = [...this.suppliers];
      }
    });

    this.lineColours$ = this.store.select(selectLineColours);
    this.lineColours$.pipe(takeUntil(this.destroy$)).subscribe((lineColors: TLineColour): void => {
      if (lineColors) {
        const none: ILineColorOption = {name: '0 - NO LINE COLOUR', color: '#FFFFFF'};
        this.lineColors = [
          none,
          ...Object.keys(lineColors).map((key: string) => ({
            name: `${key} - ${lineColors[key].description}`,
            color: lineColors[key].value || '#FFFFFF',
          })),
        ];
        this.selectLineColor(none);
        this.filteredLineColors = [...this.lineColors];
      }
    });

    this.vatRate$ = this.store.pipe(select(selectSharedGridVatRateBySelectedStore));
    this.vatRate$.pipe(takeUntil(this.destroy$)).subscribe((vatRates: VatRates): void => {
      if (vatRates) {
        this.vatRatesObject = vatRates;
        this.vatRates = this.buildVatRateOptions(vatRates);
        this.filteredVatRates = [...this.vatRates];

        const defaultVatRate = this.vatRates.find((rate) => rate.description.toLowerCase().includes('default'));
        if (defaultVatRate) {
          this.form.get('pricing.vatRate')?.setValue(defaultVatRate, {emitEvent: true});
        }
      }
    });

    // VAT rates are loaded when stock-manager opens (getVatRateConversion); may fetch missing stores async.
    this.store.select(selectAllSharedGridVatRates)
      .pipe(takeUntil(this.destroy$))
      .subscribe((vatRates: Record<string, VatRates>): void => {
        this.allVatRatesMap = vatRates || {};
      });

    // Per-store suppliers / line colours are loaded into the store on demand (see ensurePerStoreOptionsLoaded);
    // keep local snapshots so the modal can read them without the component hitting Firebase directly.
    this.store.select(selectAllStoreSuppliers)
      .pipe(takeUntil(this.destroy$))
      .subscribe((suppliers: Record<string, ISearchableFields>): void => {
        this.allStoreSuppliersMap = suppliers || {};
      });

    this.store.select(selectAllLineColours)
      .pipe(takeUntil(this.destroy$))
      .subscribe((lineColours: Record<string, TLineColour>): void => {
        this.allLineColoursMap = lineColours || {};
      });

    this.store.select(selectAllStoreDepartments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((departments: Record<string, IDepartment[]>): void => {
        this.allStoreDepartmentsMap = departments || {};
      });

    this.selectedStore$ = this.store.select(selectSelectedStore);
    this.selectedStore$.pipe(takeUntil(this.destroy$)).subscribe((store: IStore): void => {
      this.selectedStore = store;
      if (this.retryAddStockItem) {
        this.syncRetryStoreSelection();
        return;
      }
      if (store?.storeId && this.selectedStoresList.length === 0 && !this.disabledStoreIds.has(store.storeId)) {
        this.selectedStoresList = [store];
      }
    });

    this.stores$ = this.store.select(selectStores);
    this.stores$.pipe(takeUntil(this.destroy$)).subscribe((stores: IStore[]): void => {
      this.stores = stores || [];
      this.filteredStores = [...this.stores];
      if (this.retryAddStockItem) {
        this.syncRetryStoreSelection();
      }
    });

    this.priceBands$ = this.store.select(selectPriceBandingForUserSelectedStore);
    this.priceBands$.pipe(takeUntil(this.destroy$)).subscribe((priceBands: IPriceBand[]): void => {
      this.priceBands = priceBands || [];
    });

    this.allStockItems$ = this.store.select(selectAllStockItemsForCurrentSelectedStore);
    this.allStockItems$.pipe(takeUntil(this.destroy$)).subscribe((stockItems: StockItem[]): void => {
      this.allStockItems = stockItems || [];
    });

    this.store.select(selectSelectedStoreStockTags).pipe(takeUntil(this.destroy$)).subscribe((tags: {
      name: string;
      global: boolean;
    }[]): void => {
      if (tags && tags.length > 0) {
        this.allTags = [...tags];
        this.filteredTags = [...tags];
        this.tagsOptions = {};
        tags.forEach((tag: { name: string; global: boolean }) => {
          this.tagsOptions[tag.name] = {
            name: tag.name,
            selected: this.selectedTags.includes(tag.name),
            disabled: false,
          };
        });
      }
    });

    if (this.retryAddStockItem) {
      this.isRetryDataLoading = true;
      this.retryDataLoadFailed = false;

      // Set a timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (this.isRetryDataLoading) {
          console.error('[Add Stock Item] Retry data loading timeout - no data received after 15 seconds');
          this.isRetryDataLoading = false;
          this.retryDataLoadFailed = true;
        }
      }, 15000);

      this.store
        .select(selectSelectedStockViewChanges)
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (stockItem: Record<string, StockItemUpdateObj>): Promise<void> => {

          // Don't return early - keep waiting for valid data
          if (!stockItem || Object.keys(stockItem).length === 0) {
            return; // Continue listening for next emission
          }

          const stockData = Object.entries(stockItem)?.[0]?.[1]?.n;

          if (stockData && Object.keys(stockData).length > 0) {

            // Poll for dropdown data with a maximum wait time
            let attempts = 0;
            const maxAttempts = 100; // 10 seconds total (100 * 100ms)
            let allDataReady = false;

            while (attempts < maxAttempts) {
              const hasDepartments = this.departments.length > 0;
              const hasSuppliers = this.suppliers.length > 0;
              const hasLineColors = this.lineColors.length > 0;
              const hasVatRates = this.vatRates.length > 0;
              const hasTags = this.allTags.length > 0;

              if (hasDepartments && hasSuppliers && hasLineColors && hasVatRates) {
                allDataReady = true;
                break;
              }

              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }

            if (!allDataReady) {
              console.warn('[Add Stock Item] Timeout waiting for dropdown data after 10 seconds, proceeding anyway');
            }

            // Additional wait to ensure UI is stable
            await new Promise(resolve => setTimeout(resolve, 500));

            clearTimeout(loadingTimeout);

            this.populateFormFromStockData(stockData);

            // Wait a bit more before hiding loading indicator to ensure form is fully rendered
            await new Promise(resolve => setTimeout(resolve, 300));

            this.isRetryDataLoading = false;
            this.retryDataLoadFailed = false;
          }
        });
    }
  }

  public setupDropdownSearchControls(): void {
    this.storeSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterStores(term || '');
    });

    // Invalidate previously-generated codes whenever the inputs that feed code generation
    // change. The user must regenerate (which they're prompted to via the button label).
    this.form.get('basic.dep')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      if (!this.isManualCodeEntry && this.hasAnyGeneratedStockCode()) {
        this.invalidateGeneratedCodes();
      }
    });
    this.form.get('basic.subDep')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      if (!this.isManualCodeEntry && this.hasAnyGeneratedStockCode()) {
        this.invalidateGeneratedCodes();
      }
    });

    // A manually typed code is only trusted for the exact value that was checked.
    this.form.get('basic.code')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      if (this.isManualCodeEntry && this.stockCodeCheckState !== 'checking' && this.checkedStockCode !== this.manualStockCode) {
        this.resetStockCodeCheck();
      }
    });

    this.supplierSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterSuppliers(term || '');
    });

    this.departmentSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterDepartmentsWithTerm(term || '');
    });

    this.subDepartmentSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterSubDepartmentsWithTerm(term || '');
    });

    this.lineColorSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterLineColors(term || '');
    });

    this.vatRateSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterVatRates(term || '');
    });

    this.onHoldCodeSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterOnHoldCodes(term || '');
    });

    this.multiSupplierSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterMultiSuppliers(term || '');
    });

    this.tagsSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      this.filterTags(term || '');
    });

    this.barcodesSearchControl.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term: string | null): void => {
      // Barcodes don't need filtering since we're adding new ones
    });
  }

  //#endregion

  public setupPriceCalculations(): void {
    this.form
      .get('pricing.sellPriIncl1')
      ?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe((value: number): void => {
        if (value !== null && value !== undefined) {
          updatePricesFromInclusive(value, this.form);
        }
      });

    this.form
      .get('pricing.sellPriExcl1')
      ?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe((value: number): void => {
        if (value !== null && value !== undefined) {
          updatePricesFromExclusive(value, this.form);
        }
      });

    this.form
      .get('pricing.nominalGP')
      ?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe((value: number): void => {
        if (value !== null && value !== undefined) {
          const safeValue = value > 99.99 ? 99.99 : value;
          if (value > 99.99) {
            this.form.get('pricing.nominalGP')?.setValue(99.99, {emitEvent: false});
          }
          this.updatePriceFromNominalGP(safeValue);
        }
      });

    this.form
      .get('pricing.recommendedGP')
      ?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500))
      .subscribe((value: number): void => {
        if (value !== null && value !== undefined && value > 99.99) {
          this.form.get('pricing.recommendedGP')?.setValue(99.99, {emitEvent: false});
        }
      });

    this.form
      .get('pricing.vatRate')
      ?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe((vatRateObj: IVatRateOption | null): void => {
        this.updatePricingFieldsDisabledState();

        if (!vatRateObj) {
          return;
        }

        const vatRate = vatRateObj.rate || 0;

        const sellPriIncl = this.form.get('pricing.sellPriIncl1')?.value as number | null;
        const latestCost = (this.form.get('pricing.latestCost')?.value as number) || 0;

        // When VAT rate changes, ONLY recalculate exclusive price from inclusive price
        // Never change the inclusive price when VAT rate changes
        if (sellPriIncl !== null && sellPriIncl !== undefined && sellPriIncl > 0) {
          // Recalculate exclusive price based on new VAT rate (including 0% VAT)
          let calculatedExcl: number;
          if (vatRate === 0) {
            // When VAT is 0%, excl price equals incl price
            calculatedExcl = sellPriIncl;
          } else {
            calculatedExcl = sellPriIncl / (1 + vatRate / 100);
          }

          // Use proper rounding to avoid floating-point precision issues
          const roundedExcl = Math.round(calculatedExcl * 100) / 100;

          // Set exclusive price without emitting to prevent circular updates
          const exclControl = this.form.get('pricing.sellPriExcl1');
          exclControl?.setValue(roundedExcl, {emitEvent: false});
          exclControl?.markAsDirty();
          exclControl?.updateValueAndValidity({emitEvent: false});

          // Recalculate nominal GP based on new exclusive price
          if (latestCost > 0 && roundedExcl > 0) {
            const calculatedGP = ((roundedExcl - latestCost) / roundedExcl) * 100;
            const roundedGP = Math.round(calculatedGP * 100) / 100;
            const gpControl = this.form.get('pricing.nominalGP');
            gpControl?.setValue(roundedGP, {emitEvent: false});
            gpControl?.markAsDirty();
            gpControl?.updateValueAndValidity({emitEvent: false});
          }
        }
      });

    this.form
      .get('pricing.latestCost')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: number): void => {
        if (value !== null && value !== undefined) {
          this.updatePricingFieldsDisabledState();
          const sellPriIncl = this.form.get('pricing.sellPriIncl1')?.value as number | null;
          const vatRateObj = this.form.get('pricing.vatRate')?.value as IVatRateOption | null;
          if (sellPriIncl && vatRateObj && value > 0) {
            const vatRate = vatRateObj.rate || 0;
            const sellPriceExcl: number = sellPriIncl / (1 + (vatRate / 100));
            const calculatedGP: number = ((sellPriceExcl - value) / sellPriceExcl) * 100;
            this.form.get('pricing.nominalGP')?.setValue(+calculatedGP.toFixed(2), {emitEvent: false});
          }
        }
      });

    this.updatePricingFieldsDisabledState();
  }

  getFormData(path: string): string | number {
    const control = this.form.get(path);
    return control ? control.value as string | number : '';
  }

  getFormDataArray(path: string): string[] {
    const control = this.form.get(path);
    return control ? control.value as string[] : [];
  }

  public setFormValueByFieldName(fieldName: string, value: StockItemUpdateFieldValue): void {

    switch (fieldName) {
      case 'code':
        if (this.reason === NotificationResponseTypeEnum.newItemExists) break;
        this.form.get('basic.code')?.setValue(value);
        break;
      case 'desc':
        this.form.get('basic.desc')?.setValue(value);
        break;
      case 'alternateDescription':
        this.form.get('basic.alternateDescription')?.setValue(value?.toString() ?? '');
        break;
      case 'barcode':
        if (this.reason === NotificationResponseTypeEnum.duplicateBarcode) break;
        this.selectedBarcodeKeys = [value.toString()];
        this.convertBarcodesArrayToOptions(this.selectedBarcodeKeys);
        break;
      case 'additionalBarcodes':
      case 'multipleBarcodes': {
        const additional = Array.isArray(value) ? value : [];
        const primary = this.selectedBarcodeKeys[0] ?? '';
        const merged = [primary, ...additional.map((b: string | number) => b.toString().trim())].filter(Boolean);
        this.convertBarcodesArrayToOptions(merged);
        break;
      }
      case 'dep':
      case 'subDep':
        this.form.get(`basic.${fieldName}`)?.setValue(value?.toString());
        break;
      case 'onHoldCode': {
        this.form.get('basic.onHoldCode')?.setValue(value?.toString());
        if (value) this.selectedOnHoldValues = decodeOnHoldCode(+value, {returnCodes: false, isSupplier: false}) || [];
        break;
      }
      case 'lineColourCode': {
        if (value && this.lineColors.length > 0) {
          const lineColor = this.lineColors.find((c: ILineColorOption) => c.name.startsWith(value.toString()));
          if (lineColor) this.form.get('basic.lineColour')?.setValue(lineColor.name);
        }
        break;
      }
      case 'recommendedGP':
      case 'latestCost':
      case 'sellPriExcl1':
      case 'sellPriIncl1':
        this.form.get(`pricing.${fieldName}`)?.setValue(+value);
        break;
      case 'vatR': {
        if (value && this.vatRates.length > 0) {
          const vatRate = this.vatRates.find((rate) => rate.id === value.toString());
          if (vatRate) {
            this.form.get('pricing.vatRate')?.setValue(vatRate);
          } else {
            console.warn(`[Add Stock Item] VatRate not found for id: ${value}`);
          }
        } else {
          console.warn(`[Add Stock Item] Cannot set vatRate - value: ${value}, vatRates.length: ${this.vatRates.length}`);
        }
        break;
      }
      case 'packSize':
      case 'ordLvl':
      case 'maxOrderLevel':
      case 'minOrderLevel':
        this.form.get(`ordering.${fieldName}`)?.setValue(+value);
        break;
      case 'regularSuppCode':
        this.form.get('supplier.regularSuppCode')?.setValue(value);
        break;
      case 'suppCode':
        this.form.get('supplier.suppCode')?.setValue(value.toString());
        break;
      case 'multipleSuppliers':
        this.form.get('supplier.supplierCodes')?.setValue(value);
        this.form.get('supplier.multipleSuppliers')?.setValue(true);
        break;
      default:
        break;
    }
  }

  //#endregion

  //#region Form Population Methods

  //#region Dropdown Filters
  public filterDepartmentsWithTerm(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredDepartments = [...this.departments];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredDepartments = this.departments.filter((dept: IDepartment) => dept.name.toLowerCase().includes(lower) || dept.department.toString().includes(lower));
  }

  public filterSubDepartmentsWithTerm(searchTerm: string): void {
    const selectedDepartmentId = this.form.get('basic.dep')?.value;
    const lower = (searchTerm || '').toLowerCase();

    if (!searchTerm && (!this.storeSettings?.linkDepartmentsEnabled || !selectedDepartmentId)) {
      this.filteredSubDepartments = [...this.subDepartments];
      return;
    }

    if (this.storeSettings?.linkDepartmentsEnabled && selectedDepartmentId) {
      const dep = this.departments.find((d: IDepartment) => d.department.toString() === selectedDepartmentId);
      if (dep?.subDepartments) {
        const subDeps = Object.values(dep.subDepartments);
        this.filteredSubDepartments = subDeps.filter((sd: ISubDepartment) => !lower || sd.name.toLowerCase().includes(lower) || sd.subDepartment.toString().includes(lower));
      } else {
        this.filteredSubDepartments = [];
      }
    } else {
      this.filteredSubDepartments = this.subDepartments.filter(
        (sd: ISubDepartment) => !lower || sd.name.toLowerCase().includes(lower) || sd.subDepartment.toString().includes(lower),
      );
    }
  }

  public filterSuppliers(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredSuppliers = [...this.suppliers];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredSuppliers = this.suppliers.filter((option: ISupplierOption) => option.key.toLowerCase().includes(lower) || option.description?.toLowerCase().includes(lower));
  }

  public filterLineColors(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredLineColors = [...this.lineColors];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredLineColors = this.lineColors.filter((option: ILineColorOption) => option.name.toLowerCase().includes(lower));
  }

  //#endregion

  public filterVatRates(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredVatRates = [...this.vatRates];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredVatRates = this.vatRates.filter((option: IVatRateOption) => option.description.toLowerCase().includes(lower));
  }

  public filterOnHoldCodes(searchTerm: string): void {
    if (!searchTerm) {
      this.selectedOnHoldValues = [];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.selectedOnHoldValues = Object.entries(this.onHoldTypes)
      .filter(([, label]) => label !== 'NA')
      .map(([, label]) => label)
      .filter((label: string) => label.toLowerCase().includes(lower));
  }

  public filterMultiSuppliers(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredMultiSuppliers = [...this.suppliers];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredMultiSuppliers = this.suppliers.filter((option: ISupplierOption) => option.key.toLowerCase().includes(lower) || option.description?.toLowerCase().includes(lower));
  }

  public filterTags(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredTags = [...this.allTags];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredTags = this.allTags.filter((tag: { name: string; global: boolean }) => tag.name.toLowerCase().includes(lower));
  }

  //#region --> Tags Selection
  public toggleTag(tagName: string): void {
    const index = this.tempSelectedTags.indexOf(tagName);
    if (index > -1) {
      this.tempSelectedTags = this.tempSelectedTags.filter((t: string) => t !== tagName);
    } else {
      this.tempSelectedTags = [...this.tempSelectedTags, tagName];
    }
  }

  public isTagSelected(tagName: string): boolean {
    return this.tempSelectedTags.includes(tagName);
  }

  public confirmTagSelection(): void {
    this.selectedTags = [...this.tempSelectedTags];
    this.tagsSearchControl?.setValue('');
    this.showTagsDropdown = false;
  }

  public cancelTagSelection(): void {
    this.tempSelectedTags = [...this.selectedTags];
    this.showTagsDropdown = false;
  }

  public getSelectedTagsDisplay(): string {
    if (this.selectedTags.length === 0) return '';
    return this.selectedTags.join(', ');
  }

  //#endregion

  //#region --> Store Selection
  public filterStores(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredStores = [...this.stores];
      return;
    }
    const lower = searchTerm.toLowerCase();
    this.filteredStores = this.stores.filter((s: IStore) =>
      s.name?.toLowerCase().includes(lower) || s.storeId?.toLowerCase().includes(lower),
    );
  }

  public toggleStoreSelection(store: IStore): void {
    if (this.isStoreDisabled(store.storeId)) return;
    const exists = this.tempSelectedStores.some((s: IStore) => s.storeId === store.storeId);
    if (exists) {
      this.tempSelectedStores = this.tempSelectedStores.filter((s: IStore) => s.storeId !== store.storeId);
    } else {
      this.tempSelectedStores = [...this.tempSelectedStores, store];
    }
  }

  public isStoreSelected(storeId: string): boolean {
    return this.tempSelectedStores.some((s: IStore) => s.storeId === storeId);
  }

  public isStoreDisabled(storeId: string): boolean {
    return this.disabledStoreIds.has(storeId);
  }

  private hasValidAddItemSettings(settings: IAddItemRule | undefined): boolean {
    return !!settings && typeof settings.digits === 'number' && settings.digits > 0;
  }

  private async showStoresMissingSettingsWarning(missing: IStore[]): Promise<void> {
    const listItems: string[] = missing.map((s: IStore) => `• ${s.name}`);

    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Stores Missing Add Stock Item Settings',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'Some stores cannot be selected',
      contextDescription: [
        'The following store(s) do not have Add Stock Item settings configured and are disabled in the store selection:',
        Ascii.EmptySpace,
        ...listItems,
        Ascii.EmptySpace,
        'Please contact your store admin or configure these settings to add items to those stores.',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  public confirmStoreSelection(): void {
    const previousPrimaryId = this.selectedStoresList[0]?.storeId;
    this.selectedStoresList = [...this.tempSelectedStores];
    this.storeSearchControl.setValue('');
    this.showStoreDropdown = false;

    // Codes must be regenerated whenever the set of target stores changes (each store has
    // its own prefix/suffix/digits and its own running sequence).
    this.invalidateGeneratedCodes();
    // Barcodes were only cleared against the previous store set.
    this.resetBarcodeCheck();

    // Drop per-store selections for deselected stores; keep selections for stores that remain.
    // If the primary (first) store changed, clear everything — it drives auto-match, VAT lock,
    // and form mirroring, so prior per-store state is no longer valid.
    const newPrimary = this.selectedStoresList[0];
    const primaryChanged = !!newPrimary && newPrimary.storeId !== previousPrimaryId;
    this.prunePerStoreSelections(primaryChanged);

    // The first selected store drives the form's option lists (departments, suppliers, etc.).
    // If the primary changed, sync the global selected store and clear now-stale form fields.
    if (primaryChanged) {
      this.store.dispatch(setSelectedStore({selectedStore: newPrimary}));

      this.form.get('basic.dep')?.setValue('');
      this.form.get('basic.subDep')?.setValue('');
      if (!this.isManualCodeEntry) {
        this.form.get('basic.code')?.setValue('');
      }
      this.form.get('pricing.vatRate')?.setValue(null);
      this.form.get('supplier.regularSuppCode')?.setValue('');
      this.form.get('supplier.suppCode')?.setValue('');
      this.form.get('supplier.supplierCodes')?.setValue([]);
      this.selectedSuppliers = [];
      this.updatePricingFieldsDisabledState();
    }
  }

  public cancelStoreSelection(): void {
    this.tempSelectedStores = [...this.selectedStoresList];
    this.storeSearchControl.setValue('');
    this.showStoreDropdown = false;
  }

  public getSelectedStoresDisplay(): string {
    if (this.selectedStoresList.length === 0) return '';
    return this.selectedStoresList.map((s: IStore) => s.name).join(', ');
  }

  /** Locks retry mode to the failed notification's store with the correct display name. */
  private syncRetryStoreSelection(): void {
    const retryStoreId = this.retryNotification?.storeId ?? this.selectedStore?.storeId;
    if (!retryStoreId) {
      return;
    }
    const matchedStore = this.stores.find((store: IStore) => store.storeId === retryStoreId);
    if (matchedStore) {
      this.selectedStoresList = [matchedStore];
      return;
    }
    if (this.selectedStoresList.length === 0) {
      this.selectedStoresList = [{storeId: retryStoreId, name: retryStoreId}];
    }
  }

  //#endregion

  //#region --> Department and Sub-Department Selection
  public selectDepartment(department: IDepartment): void {
    this.form.get('basic.dep')?.setValue(department.department.toString());
    this.departmentSearchControl.setValue('');
    this.showDepartmentDropdown = false;

    if (this.storeSettings?.linkDepartmentsEnabled) {
      this.form.get('basic.subDep')?.setValue('');
      this.filterSubDepartmentsWithTerm('');
    }
  }

  public selectSubDepartment(subDepartment: ISubDepartment): void {
    this.form.get('basic.subDep')?.setValue(subDepartment.subDepartment.toString());
    this.subDepartmentSearchControl.setValue('');
    this.showSubDepartmentDropdown = false;
  }

  //#region --> Supplier Selection
  public selectSupplier(supplier: ISupplierOption): void {
    this.form.get('supplier.regularSuppCode')?.setValue(supplier.key);
    this.supplierSearchControl.setValue('');
    this.showSupplierDropdown = false;
  }

  //#region --> Line Color Selection
  public selectLineColor(lineColor: ILineColorOption): void {
    this.form.get('basic.lineColour')?.setValue(lineColor.name);
    this.showLineColorDropdown = false;
  }

  //#endregion

  //#region ↓ Dropdown Data Selection Methods

  //#region --> VAT Rate Selection
  public selectVatRate(vatRate: IVatRateOption): void {
    this.form.get('pricing.vatRate')?.setValue(vatRate);
    this.vatRateSearchControl.setValue('');
    this.showVatRateDropdown = false;
  }

  //#region --> On Hold Selection
  public toggleOnHoldCode(value: string): void {
    const label = this.onHoldTypes[value];
    if (!label || label === 'NA') return;

    if (this.selectedOnHoldValues.includes(label)) {
      this.selectedOnHoldValues = this.selectedOnHoldValues.filter((v: string) => v !== label);
    } else {
      this.selectedOnHoldValues = [...this.selectedOnHoldValues, label];
    }
  }

  //#endregion

  public confirmOnHoldSelection(): void {
    // An empty selection must clear the control rather than store '0'. A '0' string is truthy,
    // which would hide the placeholder behind an empty selected-value badge.
    const value = this.selectedOnHoldValues.length > 0 ? encodeOnHoldCode(this.selectedOnHoldValues).toString() : '';
    this.form.get('basic.onHoldCode')?.setValue(value);
    this.showOnHoldCodeDropdown = false;
  }

  //#endregion

  public cancelOnHoldSelection(): void {
    const currentValue = this.form.get('basic.onHoldCode')?.value;
    if (currentValue) {
      this.selectedOnHoldValues = decodeOnHoldCode(+currentValue, {returnCodes: false, isSupplier: false}) || [];
    } else {
      this.selectedOnHoldValues = [];
    }
    this.showOnHoldCodeDropdown = false;
  }

  //#endregion

  public selectBestMatchOnHoldCode(event: Event): void {
    event.preventDefault();
    this.confirmOnHoldSelection();
  }

  //#endregion

  //#region --> Select Best Match
  public selectBestMatch(event: Event, field: string): void {
    event.preventDefault();

    switch (field) {
      case 'vatRate': {
        if (this.filteredVatRates.length > 0 && (this.vatRateSearchControl.value || '').length > 0) {
          this.form.get('pricing.vatRate')?.setValue(this.filteredVatRates[0]);
        }
        this.vatRateSearchControl.setValue('');
        this.showVatRateDropdown = false;
        break;
      }
      case 'lineColour': {
        if (this.filteredLineColors.length > 0 && (this.lineColorSearchControl.value || '').length > 0) {
          this.form.get('basic.lineColour')?.setValue(this.filteredLineColors[0].name);
        }
        this.lineColorSearchControl.setValue('');
        this.showLineColorDropdown = false;
        break;
      }
      case 'regularSuppCode': {
        if (this.filteredSuppliers.length > 0 && (this.supplierSearchControl.value || '').length > 0) {
          this.form.get('supplier.regularSuppCode')?.setValue(this.filteredSuppliers[0].key);
        }
        this.supplierSearchControl.setValue('');
        this.showSupplierDropdown = false;
        break;
      }
      case 'subDep': {
        if (this.filteredSubDepartments.length > 0 && (this.subDepartmentSearchControl.value || '').length > 0) {
          this.form.get('basic.subDep')?.setValue(this.filteredSubDepartments[0].subDepartment.toString());
        }
        this.subDepartmentSearchControl.setValue('');
        this.showSubDepartmentDropdown = false;
        break;
      }
      case 'dep': {
        if (this.filteredDepartments.length > 0 && (this.departmentSearchControl.value || '').length > 0) {
          this.form.get('basic.dep')?.setValue(this.filteredDepartments[0].department.toString());
          if (this.storeSettings?.linkDepartmentsEnabled) {
            this.form.get('basic.subDep')?.setValue('');
            this.filterSubDepartmentsWithTerm('');
          }
        }
        this.departmentSearchControl.setValue('');
        this.showDepartmentDropdown = false;
        break;
      }
      default:
        break;
    }
  }

  //#region --> Department and Sub-Department Data
  public getSelectedDepartmentName(): string {
    const departmentId: string = this.form.get('basic.dep')?.value;
    if (departmentId) {
      const department = this.departments.find((dept: IDepartment) => dept.department.toString() === departmentId);
      return department ? department.name : departmentId;
    }
    return '';
  }

  public getSelectedSubDepartmentName(): string {
    const subDepartmentId: string = this.form.get('basic.subDep')?.value;
    if (subDepartmentId) {
      const subDepartment = this.subDepartments.find((sd: ISubDepartment) => sd.subDepartment.toString() === subDepartmentId);
      return subDepartment ? subDepartment.name : subDepartmentId;
    }
    return '';
  }

  //#region --> On Hold Code
  public getSelectedOnHoldCodeDisplay(): string {
    const value = this.form.get('basic.onHoldCode')?.value;
    if (!value) return '';
    const decoded = decodeOnHoldCode(+value, {returnCodes: false, isSupplier: false});
    return decoded ? decoded.join(', ') : '';
  }

  //#endregion

  //#region --> Vat Rate
  public getSelectedVatRateDescription(): string {
    const vatRateObj = this.form.get('pricing.vatRate')?.value as IVatRateOption | null;
    if (vatRateObj?.description) return vatRateObj.description;
    if (vatRateObj) {
      const found = this.vatRates.find((rate: IVatRateOption) => rate.id === vatRateObj.id);
      return found?.description || '';
    }
    return '';
  }

  //#endregion

  //#endregion

  //#region ↓ Dropdown Data Retrieval Methods

  //#region --> Line Colour
  public getSelectedLineColorName(): string {
    return (this.form.get('basic.lineColour')?.value as string) || '';
  }

  public getSelectedLineColorHex(): string {
    const lineColorName = this.form.get('basic.lineColour')?.value as string | null;
    if (lineColorName) {
      const lineColor = this.lineColors.find((c: ILineColorOption) => c.name === lineColorName);
      return lineColor ? lineColor.color : '#000000';
    }
    return '#000000';
  }

  //endregion

  public getLineColour(color: ILineColorOption): string {
    const lineColor = this.lineColors.find((c: ILineColorOption) => c.name === color.name);
    return lineColor ? lineColor.color : '#000000';
  }

  //endregion

  public getContrastColour(colour?: string): string {
    const selectedColour = colour ?? this.getSelectedLineColorHex();
    return `${GridUtils.getContrastColor(selectedColour)} !important`;
  }

  //endregion

  //#region --> Supplier
  public toggleSupplierSelection(supplier: ISupplierOption): void {
    const supplierCode = supplier.key;
    const index = this.selectedSuppliers.indexOf(supplierCode);
    if (index > -1) {
      this.selectedSuppliers = this.selectedSuppliers.filter((c: string) => c !== supplierCode);
    } else {
      this.selectedSuppliers = [...this.selectedSuppliers, supplierCode];
    }
  }

  public isSupplierSelected(supplierCode: string): boolean {
    return this.selectedSuppliers.includes(supplierCode);
  }

  public deselectAllSuppliers(storeId?: string): void {
    // If storeId provided → clear only that store
    if (storeId) {
      const updated = { ...this.selectedMultiSuppliersByStore };
      delete updated[storeId];
      this.selectedMultiSuppliersByStore = updated;
      return;
    }

    this.selectedSuppliers = [];
  }

  public confirmSupplierSelection(): void {
    this.form.get('supplier.supplierCodes')?.setValue(this.selectedSuppliers);
    this.multiSupplierSearchControl?.setValue('');
    this.showMultiSupplierDropdown = false;
  }

  //#endregion

  public cancelSupplierSelection(): void {
    const currentValue = this.form.get('supplier.supplierCodes')?.value as string[] | null;
    this.selectedSuppliers = currentValue ? [...currentValue] : [];
    this.showMultiSupplierDropdown = false;
  }

  public getSelectedSuppliersDisplay(): string {
    const supplierCodes = (this.form.get('supplier.supplierCodes')?.value as string[]) || [];
    if (supplierCodes.length === 0) return '';
    return supplierCodes
      .map((code: string) => {
        const supplier = this.suppliers.find((s: ISupplierOption) => s.key === code);
        return supplier ? `${code} - ${supplier.description}` : code;
      })
      .join(', ');
  }

  //#region --> Barcode Selection
  public onBarcodeInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.newBarcodeInput = input.value.toUpperCase();
    input.value = this.newBarcodeInput;
  }

  public addNewBarcode(): void {
    const barcode = this.newBarcodeInput.trim().toUpperCase();
    if (!barcode) return;

    // Check for duplicates in temp list
    if (this.tempSelectedBarcodes.includes(barcode)) {
      this.newBarcodeInput = '';
      return;
    }

    // Check for duplicates in existing stock items
    const duplicateCheck = this.checkForDuplicateBarcodes([barcode]);
    if (duplicateCheck.hasDuplicates) {
      void this.showDuplicateBarcodeError(duplicateCheck.duplicates);
      this.newBarcodeInput = '';
      return;
    }

    this.tempSelectedBarcodes = [...this.tempSelectedBarcodes, barcode];
    this.newBarcodeInput = '';
  }

  public removeBarcode(barcode: string): void {
    this.tempSelectedBarcodes = this.tempSelectedBarcodes.filter((b: string) => b !== barcode);
  }

  public confirmBarcodeSelection(): void {
    this.selectedBarcodeKeys = [...this.tempSelectedBarcodes];
    this.convertBarcodesArrayToOptions(this.selectedBarcodeKeys);
    this.barcodesSearchControl?.setValue('');
    this.showBarcodesDropdown = false;
    this.resetBarcodeCheck();
  }

  public cancelBarcodeSelection(): void {
    this.tempSelectedBarcodes = [...this.selectedBarcodeKeys];
    this.newBarcodeInput = '';
    this.showBarcodesDropdown = false;
  }

  public getSelectedBarcodesDisplay(): string {
    if (this.selectedBarcodeKeys.length === 0) return '';
    const primary = this.selectedBarcodeKeys[0];
    if (this.selectedBarcodeKeys.length === 1) return primary;
    return `${primary} +${this.selectedBarcodeKeys.length - 1} more`;
  }

  //#endregion

  //#region --> DropDown Handlers
  public openDropdown(dropdownKey: DropdownKey, inputRef: ElementRef | HTMLElement, dropdownHeight: number = 350): void {
    if (dropdownKey === 'store' && this.retryAddStockItem) {
      return;
    }

    const inputElement = inputRef instanceof ElementRef ? inputRef.nativeElement : inputRef;
    const inputRect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - inputRect.bottom;

    this.dropdownPositions[dropdownKey] = spaceBelow < dropdownHeight ? 'above' : 'below';

    switch (dropdownKey) {
      case 'vatRate':
        this.showVatRateDropdown = true;
        break;
      case 'supplier':
        this.showSupplierDropdown = true;
        break;
      case 'store':
        this.tempSelectedStores = [...this.selectedStoresList];
        this.filteredStores = [...this.stores];
        this.showStoreDropdown = true;
        break;
      case 'department':
        this.showDepartmentDropdown = true;
        break;
      case 'subDepartment':
        this.showSubDepartmentDropdown = true;
        break;
      case 'lineColor':
        this.showLineColorDropdown = true;
        break;
      case 'onHoldCode':
        this.showOnHoldCodeDropdown = true;
        break;
      case 'multiSupplier':
        this.showMultiSupplierDropdown = true;
        break;
      case 'tags':
        this.tempSelectedTags = [...this.selectedTags];
        this.filteredTags = [...this.allTags];
        this.showTagsDropdown = true;
        break;
      case 'barcodes':
        this.tempSelectedBarcodes = [...this.selectedBarcodeKeys];
        this.newBarcodeInput = '';
        this.showBarcodesDropdown = true;
        break;
    }
  }

  //#endregion

  public getDropdownPositionClass(dropdownKey: DropdownKey): string {
    return this.dropdownPositions[dropdownKey] === 'above' ? 'dropdown-above' : 'dropdown-below';
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const clickedSelect = target.closest('.custom-select');
    const clickedSupplier = target.closest('.supplier-selector');

    const clickedStoreDropdown = clickedSelect?.contains(this.storeInput?.nativeElement as Node);
    const clickedDepartmentDropdown = clickedSelect?.contains(this.departmentInput?.nativeElement as Node);
    const clickedSubDepartmentDropdown = clickedSelect?.contains(this.subDepartmentInput?.nativeElement as Node);
    const clickedLineColorDropdown = clickedSelect?.contains(this.lineColorInput?.nativeElement as Node);
    const clickedOnHoldCodeDropdown = clickedSelect?.contains(this.onHoldCodeInput?.nativeElement as Node);
    const clickedVatRateDropdown = clickedSelect?.contains(this.vatRateInput?.nativeElement as Node);
    const clickedMultiSupplierDropdown = clickedSelect?.contains(this.multiSupplierInput?.nativeElement as Node);
    const clickedTagsDropdown = clickedSelect?.contains(this.tagsInput?.nativeElement as Node);
    const clickedBarcodesDropdown = clickedSelect?.contains(this.barcodesInput?.nativeElement as Node);
    const clickedSupplierDropdown = clickedSupplier?.contains(this.supplierInput?.nativeElement as Node);

    if (this.showStoreDropdown && !clickedStoreDropdown) {
      const clickedDropdownActions = target.closest('.dropdown-actions');
      if (!clickedDropdownActions) this.showStoreDropdown = false;
    }
    if (this.showDepartmentDropdown && !clickedDepartmentDropdown) this.showDepartmentDropdown = false;
    if (this.showSubDepartmentDropdown && !clickedSubDepartmentDropdown) this.showSubDepartmentDropdown = false;
    if (this.showSupplierDropdown && !clickedSupplierDropdown) this.showSupplierDropdown = false;
    if (this.showLineColorDropdown && !clickedLineColorDropdown) this.showLineColorDropdown = false;
    if (this.showVatRateDropdown && !clickedVatRateDropdown) this.showVatRateDropdown = false;
    if (this.showOnHoldCodeDropdown && !clickedOnHoldCodeDropdown) this.showOnHoldCodeDropdown = false;

    if (this.showMultiSupplierDropdown && !clickedMultiSupplierDropdown) {
      const clickedDropdownActions = target.closest('.dropdown-actions');
      if (!clickedDropdownActions) this.showMultiSupplierDropdown = false;
    }

    if (this.showTagsDropdown && !clickedTagsDropdown) {
      const clickedDropdownActions = target.closest('.dropdown-actions');
      if (!clickedDropdownActions) this.showTagsDropdown = false;
    }

    if (this.showBarcodesDropdown && !clickedBarcodesDropdown) {
      const clickedDropdownActions = target.closest('.dropdown-actions');
      if (!clickedDropdownActions) this.showBarcodesDropdown = false;
    }

    if (this.openPerStoreDropdownStoreId && !target.closest('.per-store-select')) {
      this.openPerStoreDropdownStoreId = null;
    }
  }

  public updatePricingFieldsDisabledState(): void {
    const latestCost = this.form.get('pricing.latestCost')?.value as number | null;
    const vatRate = this.form.get('pricing.vatRate')?.value as IVatRateOption | null;
    const shouldDisable = !latestCost || !vatRate;

    const nominalGPControl = this.form.get('pricing.nominalGP');
    const sellPriIncl1Control = this.form.get('pricing.sellPriIncl1');
    const sellPriExcl1Control = this.form.get('pricing.sellPriExcl1');

    // Silent toggles: enable() / disable() emit valueChanges by default, which would re-enter the
    // price recalculation subscriptions below with the control's stale value.
    const silentUpdate = {emitEvent: false};
    if (shouldDisable) {
      nominalGPControl?.disable(silentUpdate);
      sellPriIncl1Control?.disable(silentUpdate);
      sellPriExcl1Control?.disable(silentUpdate);
    } else {
      nominalGPControl?.enable(silentUpdate);
      sellPriIncl1Control?.enable(silentUpdate);
      sellPriExcl1Control?.enable(silentUpdate);
    }
  }

  public updatePriceFromNominalGP(gpValue: number): void {
    const vatRateObj = this.form.get('pricing.vatRate')?.value as IVatRateOption | null;
    const latestCost = (this.form.get('pricing.latestCost')?.value as number) || 0;

    if (!vatRateObj || latestCost === 0) return;

    const stockItem = {nominalGP: gpValue, latestCost, vatR: vatRateObj.id} as unknown as StockItem;
    const calculatedPrice = calculatePriceFromGpValue(this.selectedStore, stockItem);

    // Use proper rounding to avoid floating-point precision issues
    const roundedInclPrice = Math.round(calculatedPrice * 100) / 100;
    this.form.get('pricing.sellPriIncl1')?.setValue(roundedInclPrice, {emitEvent: false});

    const vatRate = vatRateObj.rate || 0;
    let exclPrice: number;
    if (vatRate === 0) {
      exclPrice = roundedInclPrice;
    } else {
      exclPrice = roundedInclPrice / (1 + vatRate / 100);
    }
    const roundedExclPrice = Math.round(exclPrice * 100) / 100;
    this.form.get('pricing.sellPriExcl1')?.setValue(roundedExclPrice, {emitEvent: false});
  }

  public onApplyPriceBandingClick(): void {
    if (!this.selectedStore || !isNonEmptyObject(this.vatRatesObject)) {
      console.warn('Cannot apply price banding: Missing required data (store or VAT rates)');
      return;
    }

    const priceBandsArray = this.priceBands;
    if (!priceBandsArray || priceBandsArray.length === 0) {
      console.warn('Cannot apply price banding: No price bands configured');
      return;
    }

    const stockItem = createStockItemFromForm(this.form);
    const originalSellPrice = stockItem.sellPriIncl1;
    const originalNominalGP = stockItem.nominalGP;

    // Create a copy with originalValue set (required for applyPriceBanding to work)
    const stockItemWithOriginal = getDeepCopyOfObject(stockItem);

    // Set originalValue with DIFFERENT values so applyPriceBanding detects a change
    // This is necessary because the function only applies banding if values have been modified
    stockItemWithOriginal.originalValue = {
      sellPriIncl1: {value: 0},
      nominalGP: {value: 0},
    };

    const banded = applyPriceBanding(
      stockItemWithOriginal,
      priceBandsArray,
      ['sellPriIncl1', 'nominalGP'],
      this.selectedStore,
      this.vatRatesObject,
    );

    this.updateFormWithBandedPrices(banded);
  }

  //#endregion

  public updateFormWithBandedPrices(bandedItem: StockItem): void {
    this.form.get('pricing.sellPriIncl1')?.setValue(bandedItem.sellPriIncl1, {emitEvent: false});

    const vatRateObj = this.form.get('pricing.vatRate')?.value as IVatRateOption | null;
    const vatRate: number = vatRateObj?.rate || 0;
    const latestCost = (this.form.get('pricing.latestCost')?.value as number) || 0;

    if (bandedItem.nominalGP !== undefined) {
      this.form.get('pricing.nominalGP')?.setValue(bandedItem.nominalGP, {emitEvent: false});
    } else if (latestCost > 0 && vatRate > 0) {
      const sellPriceExcl: number = bandedItem.sellPriIncl1 / (1 + (vatRate / 100));
      const calculatedGP: number = ((sellPriceExcl - latestCost) / sellPriceExcl) * 100;
      this.form.get('pricing.nominalGP')?.setValue(+calculatedGP.toFixed(2), {emitEvent: false});
    }

    if (vatRate > 0) {
      const exclPrice = calculateExclusivePrice(bandedItem.sellPriIncl1, vatRate);
      this.form.get('pricing.sellPriExcl1')?.setValue(exclPrice, {emitEvent: false});
    }
  }

  //#region Manual Stock Code Entry

  /**
   * Seed the manual-entry checkbox from the selected store's Add Item Stock Rule. The selector
   * emits a placeholder (digits 0) before the store rule loads, so the default is applied only
   * once valid settings arrive and is not reapplied if the user later changes the checkbox.
   */
  private applyManualCodeEntryDefault(settings: IAddItemRule | undefined): void {
    if (this.hasAppliedManualCodeEntryDefault || !settings || !this.hasValidAddItemSettings(settings)) {
      return;
    }
    this.hasAppliedManualCodeEntryDefault = true;
    if (settings.manualCodeEntryDefault) {
      this.form.get('basic.manualCodeEntry')?.setValue(true, {emitEvent: false});
    }
  }

  /** Switching between manual and generated codes always discards the current code. */
  public onManualCodeEntryToggle(): void {
    this.generatedCodesByStore = {};
    this.form.get('basic.code')?.setValue('');
    this.resetStockCodeCheck();
  }

  public resetStockCodeCheck(): void {
    this.stockCodeCheckState = 'unchecked';
    this.stockCodeConflicts = [];
    this.checkedStockCode = '';
  }

  public canCheckStockCode(): boolean {
    return this.stockCodeCheckState !== 'checking'
      && this.manualStockCode.length > 0
      && this.selectedStoresList.length > 0;
  }

  /** True once the typed code has been confirmed as unused in every selected store. */
  public isManualStockCodeVerified(): boolean {
    return this.stockCodeCheckState === 'available' && this.checkedStockCode === this.manualStockCode;
  }

  public getStockCodeCheckMessage(): string {
    const storeCount = this.selectedStoresList.length;
    const storeLabel = storeCount === 1 ? 'the selected store' : `all ${storeCount} selected stores`;
    switch (this.stockCodeCheckState) {
      case 'available':
        return `"${this.checkedStockCode}" is available in ${storeLabel}.`;
      case 'taken': {
        const conflictCount = this.stockCodeConflicts.length;
        const conflictLabel = conflictCount === 1 ? 'store' : 'stores';
        return `"${this.checkedStockCode}" already exists in ${conflictCount} selected ${conflictLabel}:`;
      }
      default:
        return '';
    }
  }

  /**
   * Looks the typed code up in every selected store's stock collection. The item cannot be
   * submitted until this reports the code as unused everywhere.
   */
  public async onCheckStockCodeAvailability(): Promise<void> {
    if (!this.canCheckStockCode()) {
      return;
    }

    const stockCode = this.manualStockCode;
    this.stockCodeCheckState = 'checking';
    this.stockCodeConflicts = [];

    try {
      const result: IStockManagerStockCodeAvailability = await this.stockCodeAvailabilityService.checkStockCodeAvailability(
        this.selectedStoresList.map((s: IStore) => ({storeId: s.storeId, name: s.name})),
        stockCode,
      );

      this.checkedStockCode = result.stockCode;
      this.stockCodeConflicts = result.conflicts;
      this.stockCodeCheckState = result.isAvailable ? 'available' : 'taken';

      if (!result.isAvailable) {
        await this.showStockCodeConflictError(result);
      }
    } catch (error) {
      console.error('[Add Stock Item] Failed to check stock code availability', error);
      this.resetStockCodeCheck();
      await this.showStockCodeCheckFailedError();
    }
  }

  private async showStockCodeConflictError(result: IStockManagerStockCodeAvailability): Promise<void> {
    const conflictLines = result.conflicts.map(
      (conflict: IStockManagerStockCodeConflict) => `• ${conflict.storeName} — ${conflict.existingStockDescription}`,
    );

    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Stock Code Already Exists',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Cannot Add Stock Item',
      contextDescription: [
        `Stock code "${result.stockCode}" already exists in the following store(s):`,
        Ascii.EmptySpace,
        ...conflictLines,
        Ascii.EmptySpace,
        'Enter a different stock code, or remove those stores from the selection, then check again.',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private async showStockCodeCheckFailedError(): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Stock Code Check Failed',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Could not check the stock code',
      contextDescription: [
        'The stock code could not be checked against the selected stores.',
        'Please check your connection and try again.',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  //#endregion

  //#region Barcode Availability

  public resetBarcodeCheck(): void {
    this.barcodeCheckState = 'unchecked';
    this.barcodeConflicts = [];
    this.checkedBarcodeKey = '';
  }

  public canCheckBarcodes(): boolean {
    return this.barcodeCheckState !== 'checking'
      && this.selectedBarcodeKeys.length > 0
      && this.selectedStoresList.length > 0;
  }

  /** True once the current barcode list has been confirmed as unused in every selected store. */
  public areBarcodesVerified(): boolean {
    return this.barcodeCheckState === 'available' && this.checkedBarcodeKey === this.barcodeCheckKey;
  }

  public getBarcodeCheckMessage(): string {
    const storeCount = this.selectedStoresList.length;
    const storeLabel = storeCount === 1 ? 'the selected store' : `all ${storeCount} selected stores`;
    switch (this.barcodeCheckState) {
      case 'available':
        return this.selectedBarcodeKeys.length === 1
          ? `This barcode is available in ${storeLabel}.`
          : `All ${this.selectedBarcodeKeys.length} barcodes are available in ${storeLabel}.`;
      case 'taken':
        return 'The following barcodes are already in use:';
      default:
        return '';
    }
  }

  /**
   * Checks every entered barcode against the primary and additional barcodes of each selected
   * store. Mirrors the manual stock code check: the item cannot be submitted until this passes.
   */
  public async onCheckBarcodesAvailability(): Promise<boolean> {
    if (!this.canCheckBarcodes()) {
      return false;
    }

    const barcodeKey = this.barcodeCheckKey;
    this.barcodeCheckState = 'checking';
    this.barcodeConflicts = [];

    try {
      const result: IStockManagerBarcodeAvailability = await this.barcodeAvailabilityService.checkBarcodeAvailability(
        this.selectedStoresList.map((s: IStore) => ({storeId: s.storeId, name: s.name})),
        this.selectedBarcodeKeys,
      );

      this.checkedBarcodeKey = barcodeKey;
      this.barcodeConflicts = result.conflicts;
      this.barcodeCheckState = result.isAvailable ? 'available' : 'taken';
      return result.isAvailable;
    } catch (error) {
      console.error('[Add Stock Item] Failed to check barcode availability', error);
      this.resetBarcodeCheck();
      await this.showBarcodeCheckFailedError();
      return false;
    }
  }

  /** Human readable line for a barcode conflict, used in both the inline list and the modal. */
  public getBarcodeConflictLabel(conflict: IStockManagerBarcodeStoreConflict): string {
    const barcodeType = conflict.isPrimaryBarcode ? 'primary' : 'additional';
    return `${conflict.barcode} — ${conflict.storeName}: ${barcodeType} barcode on ${conflict.existingStockCode} (${conflict.existingStockDescription})`;
  }

  private async showBarcodeStoreConflictError(conflicts: IStockManagerBarcodeStoreConflict[]): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Duplicate Barcode Detected',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Cannot Add Stock Item',
      contextDescription: [
        'The following barcode(s) already exist in the selected store(s):',
        Ascii.EmptySpace,
        ...conflicts.map((conflict: IStockManagerBarcodeStoreConflict) => `• ${this.getBarcodeConflictLabel(conflict)}`),
        Ascii.EmptySpace,
        'Please use unique barcodes, or remove those stores from the selection, then check again.',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private async showBarcodeCheckFailedError(): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Barcode Check Failed',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Could not check the barcodes',
      contextDescription: [
        'The barcodes could not be checked against the selected stores.',
        'Please check your connection and try again.',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  //#endregion

  //#region Code Generation

  private hasAnyGeneratedStockCode(): boolean {
    return Object.keys(this.generatedCodesByStore).length > 0 || !!this.form.get('basic.code')?.value;
  }

  public hasGeneratedCodesForAllSelectedStores(): boolean {
    if (this.selectedStoresList.length === 0) return false;
    if (this.isManualCodeEntry) {
      return this.isManualStockCodeVerified();
    }
    if (!this.isMultiStore) {
      return !!(this.form.get('basic.code')?.value as string)?.trim();
    }
    return this.selectedStoresList.every((s: IStore) => !!this.generatedCodesByStore[s.storeId]);
  }

  public invalidateGeneratedCodes(): void {
    this.generatedCodesByStore = {};
    // A manually typed code survives store / department changes, but its availability result
    // does not — the new store set may already use it.
    if (this.isManualCodeEntry) {
      this.resetStockCodeCheck();
      return;
    }
    this.form.get('basic.code')?.setValue('');
  }

  /**
   * Build a code-generation rule for a single store using **that store's** add-item settings.
   * `prefix` / `suffix` can be the literal values `'dep'` or `'subDep'`, in which case the
   * current form values for department / sub-department are substituted in.
   */
  private buildStockCodeRuleForStore(store: IStore): IStoreStockCodeRule | null {
    const settings = this.allAddItemSettingsMap[store.storeId];
    if (!settings || !this.hasValidAddItemSettings(settings)) return null;

    const depValue = this.isMultiStore
      ? (this.selectedDepartmentByStore[store.storeId]?.department.toString() ?? '')
      : ((this.form.get('basic.dep')?.value as string) || '');
    const subDepValue = this.isMultiStore
      ? (this.selectedSubDepartmentByStore[store.storeId]?.subDepartment.toString() ?? '')
      : ((this.form.get('basic.subDep')?.value as string) || '');

    const resolveToken = (token: string | undefined): string => {
      // A literal 'none' setting means "no prefix/suffix" — contribute nothing to the code.
      if (!token || token.toLowerCase() === 'none') return '';
      if (token === 'dep') return depValue;
      if (token === 'subDep') return subDepValue;
      return token;
    };

    return {
      storeId: store.storeId,
      prefix: resolveToken(settings.prefix),
      suffix: resolveToken(settings.suffix),
      digits: settings.digits,
    };
  }

  public onGenerateStockCodeClick(): void {
    // If we already have codes, this button shows the modal instead of regenerating.
    if (this.hasGeneratedCodesForAllSelectedStores()) {
      void this.openGeneratedCodesModal();
      return;
    }

    if (this.selectedStoresList.length === 0) return;

    const rules: IStoreStockCodeRule[] = this.selectedStoresList
      .map((s: IStore) => this.buildStockCodeRuleForStore(s))
      .filter((r: IStoreStockCodeRule | null): r is IStoreStockCodeRule => r !== null);

    if (rules.length === 0) {
      console.warn('[Add Stock Item] No selected store has valid add-item settings; cannot generate codes');
      return;
    }

    this.isGettingItemCode = true;

    this.gallixApiService
      .generateNextStockCode(rules)
      .pipe(take(1))
      .subscribe({
        next: (res: INextStockCodesResponse): void => {
          this.isGettingItemCode = false;
          this.generatedCodesByStore = {};
          Object.entries(res || {}).forEach(([storeId, code]: [string, string]) => {
            if (code) this.generatedCodesByStore[storeId] = code.toString();
          });

          const primaryStoreId = this.selectedStoresList[0]?.storeId;
          let primaryCode = primaryStoreId ? this.generatedCodesByStore[primaryStoreId] ?? '' : '';
          if (!primaryCode && !this.isMultiStore) {
            primaryCode = Object.values(res || {}).find(Boolean)?.toString() ?? '';
            if (primaryStoreId && primaryCode) {
              this.generatedCodesByStore[primaryStoreId] = primaryCode;
            }
          }

          this.form.get('basic.code')?.setValue(primaryCode);
        },
        error: (e): void => {
          console.error(e?.message ?? e);
          this.isGettingItemCode = false;
        },
      });
  }

  public async openGeneratedCodesModal(): Promise<void> {
    const lines = this.selectedStoresList.map((s: IStore) => {
      const code = this.generatedCodesByStore[s.storeId]
        ?? (!this.isMultiStore ? (this.form.get('basic.code')?.value as string) : undefined);
      return code ? `${code}  —  ${s.name}` : `(no code generated)  —  ${s.name}`;
    });

    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Regenerate',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Generated Stock Codes',
      modalTitleIcon: IconsEnum.settings,
      modalTitleIconColor: IconColourEnum.green,
      contextHeading: this.selectedStoresList.length === 1
        ? 'Generated stock code'
        : `Generated stock codes for ${this.selectedStoresList.length} stores`,
      contextDescription: lines,
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });

    await modal.present();
    const {role} = await modal.onDidDismiss();
    if (role === 'modal-accept') {
      this.invalidateGeneratedCodes();
      this.onGenerateStockCodeClick();
    }
  }

  //#endregion

  //#region Per-store field selection (Supplier / Line Colour / VAT Rate)

  /**
   * Trim per-store selections when the store set changes. Deselected stores are dropped; stores
   * that remain selected keep their configured values. When the primary store changes, everything
   * is cleared because the first store drives auto-match, VAT percentage lock, and form mirroring.
   */
  private prunePerStoreSelections(primaryChanged: boolean = false): void {
    if (primaryChanged) {
      this.selectedSupplierByStore = {};
      this.selectedLineColourByStore = {};
      this.selectedVatRateByStore = {};
      this.selectedMultiSuppliersByStore = {};
      this.selectedDepartmentByStore = {};
      this.selectedSubDepartmentByStore = {};
      return;
    }

    const selectedIds = new Set(this.selectedStoresList.map((s: IStore) => s.storeId));
    this.selectedSupplierByStore = this.retainPerStoreKeys(this.selectedSupplierByStore, selectedIds);
    this.selectedLineColourByStore = this.retainPerStoreKeys(this.selectedLineColourByStore, selectedIds);
    this.selectedVatRateByStore = this.retainPerStoreKeys(this.selectedVatRateByStore, selectedIds);
    this.selectedMultiSuppliersByStore = this.retainPerStoreKeys(this.selectedMultiSuppliersByStore, selectedIds);
    this.selectedDepartmentByStore = this.retainPerStoreKeys(this.selectedDepartmentByStore, selectedIds);
    this.selectedSubDepartmentByStore = this.retainPerStoreKeys(this.selectedSubDepartmentByStore, selectedIds);
  }

  private retainPerStoreKeys<T>(record: Record<string, T>, selectedIds: Set<string>): Record<string, T> {
    const retained: Record<string, T> = {};
    for (const storeId of selectedIds) {
      if (storeId in record) {
        retained[storeId] = record[storeId];
      }
    }
    return retained;
  }

  public getPerStoreFieldLabel(field: PerStoreField): string {
    switch (field) {
      case 'supplier':
        return 'Suppliers';
      case 'lineColour':
        return 'Line Colours';
      case 'vatRate':
        return 'VAT Rates';
      case 'multiSupplier':
        return 'Multiple Suppliers';
      case 'department':
        return 'Departments';
      case 'subDepartment':
        return 'Sub-Departments';
    }
  }

  /** Label for the field button shown in place of the inline dropdown in multi-store mode. */
  public getPerStoreButtonLabel(field: PerStoreField): string {
    const label = this.getPerStoreFieldLabel(field);
    return this.hasAnyPerStoreSelection(field) ? `View ${label}` : `Select ${label}`;
  }

  public hasAnyPerStoreSelection(field: PerStoreField): boolean {
    return this.selectedStoresList.some((s: IStore) => !!this.getSelectionForField(field, s.storeId));
  }

  private getSelectionForField(
    field: PerStoreField,
    storeId: string,
  ): ISupplierOption | ILineColorOption | IVatRateOption | IDepartment | ISubDepartment | ISupplierOption[] | null {
    switch (field) {
      case 'supplier':
        return this.selectedSupplierByStore[storeId] ?? null;
      case 'lineColour':
        return this.selectedLineColourByStore[storeId] ?? null;
      case 'vatRate':
        return this.selectedVatRateByStore[storeId] ?? null;
      case 'department':
        return this.selectedDepartmentByStore[storeId] ?? null;
      case 'subDepartment':
        return this.selectedSubDepartmentByStore[storeId] ?? null;
      case 'multiSupplier': {
        const arr = this.selectedMultiSuppliersByStore[storeId];
        return arr && arr.length > 0 ? arr : null;
      }
    }
  }

  public async openPerStoreModal(field: PerStoreField): Promise<void> {
    if (this.selectedStoresList.length === 0) return;
    this.activePerStoreField = field;
    this.openPerStoreDropdownStoreId = null;
    this.perStoreSearchTerm = {};

    await this.ensurePerStoreOptionsLoaded(field);
    this.seedPerStoreSelectionsFromForm(field);

    const primaryId = this.selectedStoresList[0]?.storeId;
    if (primaryId && this.getSelectionForField(field, primaryId)) {
      this.applyAutoMatch(field, primaryId);
    }
  }

  public closePerStoreModal(): void {
    this.activePerStoreField = null;
    this.openPerStoreDropdownStoreId = null;
  }

  public confirmPerStoreModal(): void {
    if (this.activePerStoreField) this.mirrorPrimaryPerStoreToForm(this.activePerStoreField);
    this.closePerStoreModal();
  }

  /** Closes the open in-modal dropdown when clicking elsewhere inside the modal. */
  public onPerStoreModalClick(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (this.openPerStoreDropdownStoreId && !target.closest('.per-store-select')) {
      this.openPerStoreDropdownStoreId = null;
    }
  }

  /**
   * Make sure each selected store's options are available. Suppliers and line colours are loaded
   * through NgRx (dispatch a per-store load, then read from the store) so the component never calls
   * Firebase directly; VAT rates are already in the store from the page-level load.
   */
  private async ensurePerStoreOptionsLoaded(field: PerStoreField): Promise<void> {
    this.isLoadingPerStoreData = true;
    try {
      if (field === 'supplier' || field === 'multiSupplier') {
        this.selectedStoresList
          .filter((s: IStore) => !this.allStoreSuppliersMap[s.storeId])
          .forEach((s: IStore) => {
            this.store.dispatch(getStoreSuppliers({store: s}));
          });
        const suppliersMap = await firstValueFrom(this.store.select(selectAllStoreSuppliers).pipe(
          filter((m: Record<string, ISearchableFields>) => this.selectedStoresList.every((s: IStore) => !!m[s.storeId])),
          take(1),
        ));
        this.selectedStoresList.forEach((s: IStore) => {
          this.suppliersByStore[s.storeId] = objectToSupplierOptions(suppliersMap[s.storeId]);
        });
      } else if (field === 'lineColour') {
        this.selectedStoresList
          .filter((s: IStore) => !(s.storeId in this.allLineColoursMap))
          .forEach((s: IStore) => {
            this.store.dispatch(getStoreLineColours({store: s}));
          });
        const lineColoursMap = await firstValueFrom(this.store.select(selectAllLineColours).pipe(
          filter((m: Record<string, TLineColour>) => this.selectedStoresList.every((s: IStore) => s.storeId in m)),
          take(1),
        ));
        this.selectedStoresList.forEach((s: IStore) => {
          this.lineColoursByStore[s.storeId] = this.buildLineColourOptions(lineColoursMap[s.storeId]);
        });
      } else if (field === 'vatRate') {
        const missingVatStores = this.selectedStoresList.filter(
          (s: IStore) => !this.allVatRatesMap[s.storeId],
        );
        if (missingVatStores.length > 0) {
          this.store.dispatch(getVatRateConversion());
          const vatRatesMap = await firstValueFrom(
            this.store.select(selectAllSharedGridVatRates).pipe(
              filter((m: Record<string, VatRates>) =>
                this.selectedStoresList.every((s: IStore) => !!m[s.storeId])),
              take(1),
            ),
          );
          this.allVatRatesMap = vatRatesMap || this.allVatRatesMap;
        }
        this.selectedStoresList.forEach((s: IStore) => {
          this.vatRatesByStore[s.storeId] = this.buildVatRateOptions(this.allVatRatesMap[s.storeId]);
        });
      } else if (field === 'department' || field === 'subDepartment') {
        this.selectedStoresList
          .filter((s: IStore) => !this.allStoreDepartmentsMap[s.storeId])
          .forEach((s: IStore) => {
            this.store.dispatch(getStoreDepartments({store: s}));
          });
        const departmentsMap = await firstValueFrom(this.store.select(selectAllStoreDepartments).pipe(
          filter((m: Record<string, IDepartment[]>) => this.selectedStoresList.every((s: IStore) => !!m[s.storeId])),
          take(1),
        ));
        this.selectedStoresList.forEach((s: IStore) => {
          this.departmentsByStore[s.storeId] = departmentsMap[s.storeId] || [];
        });
      }
    } catch (e) {
      console.error('[Add Stock Item] Failed to load per-store options', e);
    } finally {
      this.isLoadingPerStoreData = false;
    }
  }

  private buildLineColourOptions(lineColours: TLineColour): ILineColorOption[] {
    const none: ILineColorOption = {name: '0 - NO LINE COLOUR', color: '#FFFFFF'};
    if (!lineColours) return [none];
    return [
      none,
      ...Object.keys(lineColours).map((key: string) => ({
        name: `${key} - ${lineColours[key].description}`,
        color: lineColours[key].value || '#FFFFFF',
      })),
    ];
  }

  private buildVatRateOptions(vatRates: VatRates): IVatRateOption[] {
    if (!vatRates) return [];
    return Object.keys(vatRates).map((key: string) => {
      const entry = vatRates[key] as { vatRate: number; desc?: string; description?: string };
      const label = entry?.description ?? entry?.desc ?? '';
      return {
        id: key,
        rate: entry.vatRate,
        description: `${label} - ${entry.vatRate}%`,
      };
    });
  }

  /** Seed the primary store's selection from the inline form value (so single→multi carries over). */
  private seedPerStoreSelectionsFromForm(field: PerStoreField): void {
    const primaryId = this.selectedStoresList[0]?.storeId;
    if (!primaryId) return;

    if (field === 'supplier' && this.selectedSupplierByStore[primaryId] === undefined) {
      const code = (this.form.get('supplier.regularSuppCode')?.value as string) || '';
      const opt = code ? (this.suppliersByStore[primaryId] || []).find((o: ISupplierOption) => o.key === code) : null;
      this.selectedSupplierByStore[primaryId] = opt ?? (code ? {key: code, description: ''} : null);
    } else if (field === 'lineColour' && this.selectedLineColourByStore[primaryId] === undefined) {
      const name = (this.form.get('basic.lineColour')?.value as string) || '';
      const opt = name ? (this.lineColoursByStore[primaryId] || []).find((o: ILineColorOption) => o.name === name) : null;
      this.selectedLineColourByStore[primaryId] = opt ?? null;
    } else if (field === 'vatRate' && this.selectedVatRateByStore[primaryId] === undefined) {
      const vat = this.form.get('pricing.vatRate')?.value as IVatRateOption | null;
      const opt = vat ? (this.vatRatesByStore[primaryId] || []).find((o: IVatRateOption) => o.id === vat.id) : null;
      this.selectedVatRateByStore[primaryId] = opt ?? vat ?? null;
    } else if (field === 'department' && this.selectedDepartmentByStore[primaryId] === undefined) {
      const depId = (this.form.get('basic.dep')?.value as string) || '';
      const opt = depId
        ? (this.departmentsByStore[primaryId] || []).find((d: IDepartment) => d.department.toString() === depId)
        : null;
      this.selectedDepartmentByStore[primaryId] = opt ?? null;
    } else if (field === 'subDepartment' && this.selectedSubDepartmentByStore[primaryId] === undefined) {
      const subDepId = (this.form.get('basic.subDep')?.value as string) || '';
      const depId = (this.form.get('basic.dep')?.value as string) || '';
      const subDeps = depId ? this.getSubDepartmentsForStoreDepartment(primaryId, depId) : [];
      const opt = subDepId
        ? subDeps.find((sd: ISubDepartment) => sd.subDepartment.toString() === subDepId)
        : null;
      this.selectedSubDepartmentByStore[primaryId] = opt ?? null;
    }
    // 'multiSupplier' is intentionally not seeded from the form: additional suppliers are chosen
    // per store and are independent of the regular supplier.
  }

  //#region --> In-modal dropdown interaction
  public openPerStoreDropdown(storeId: string): void {
    this.openPerStoreDropdownStoreId = storeId;
  }

  public onPerStoreSearch(storeId: string, event: Event): void {
    this.perStoreSearchTerm[storeId] = (event.target as HTMLInputElement).value || '';
    this.openPerStoreDropdownStoreId = storeId;
  }

  public getPerStoreFilteredSuppliers(storeId: string): ISupplierOption[] {
    const options = this.suppliersByStore[storeId] || [];
    const term = (this.perStoreSearchTerm[storeId] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((o: ISupplierOption) =>
      o.key.toLowerCase().includes(term) || (o.description || '').toLowerCase().includes(term));
  }

  public getPerStoreFilteredLineColours(storeId: string): ILineColorOption[] {
    const options = this.lineColoursByStore[storeId] || [];
    const term = (this.perStoreSearchTerm[storeId] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((o: ILineColorOption) => o.name.toLowerCase().includes(term));
  }

  public getPerStoreFilteredVatRates(storeId: string): IVatRateOption[] {
    let options = this.vatRatesByStore[storeId] || [];
    // All stores must share one VAT percentage. The controlling (first) store may pick any rate;
    // every other store is restricted to options at the controlling store's selected percentage.
    const lockedRate = this.getControllingVatRate(storeId);
    if (lockedRate !== null) {
      options = options.filter((o: IVatRateOption) => o.rate === lockedRate);
    }
    const term = (this.perStoreSearchTerm[storeId] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((o: IVatRateOption) => o.description.toLowerCase().includes(term));
  }

  public getPerStoreFilteredDepartments(storeId: string): IDepartment[] {
    const options = this.departmentsByStore[storeId] || [];
    const term = (this.perStoreSearchTerm[storeId] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((d: IDepartment) =>
      d.name.toLowerCase().includes(term) || d.department.toString().includes(term));
  }

  public getPerStoreFilteredSubDepartments(storeId: string): ISubDepartment[] {
    const department = this.selectedDepartmentByStore[storeId];
    if (!department) return [];
    const options = this.getSubDepartmentsForStoreDepartment(storeId, department.department.toString());
    const term = (this.perStoreSearchTerm[storeId] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((sd: ISubDepartment) =>
      sd.name.toLowerCase().includes(term) || sd.subDepartment.toString().includes(term));
  }

  private getSubDepartmentsForStoreDepartment(storeId: string, departmentId: string): ISubDepartment[] {
    const departments = this.departmentsByStore[storeId] || [];
    const department = departments.find((d: IDepartment) => d.department.toString() === departmentId);
    if (!department?.subDepartments) return [];
    return Array.isArray(department.subDepartments)
      ? department.subDepartments
      : Object.values(department.subDepartments);
  }

  /**
   * The VAT percentage a store is locked to, or null if unrestricted. The first selected store is
   * the controller (unrestricted); all other stores are locked to the controller's selected rate.
   */
  private getControllingVatRate(storeId: string): number | null {
    const primaryId = this.selectedStoresList[0]?.storeId;
    if (!primaryId || storeId === primaryId) return null;
    return this.selectedVatRateByStore[primaryId]?.rate ?? null;
  }

  public selectPerStoreSupplier(storeId: string, option: ISupplierOption): void {
    this.selectedSupplierByStore[storeId] = option;
    this.afterPerStoreSelection('supplier', storeId);
  }

  public selectPerStoreLineColour(storeId: string, option: ILineColorOption): void {
    this.selectedLineColourByStore[storeId] = option;
    this.afterPerStoreSelection('lineColour', storeId);
  }

  public selectPerStoreDepartment(storeId: string, department: IDepartment): void {
    const previousDep = this.selectedDepartmentByStore[storeId]?.department;
    const departmentChanged = previousDep !== department.department;
    this.selectedDepartmentByStore[storeId] = department;
    if (departmentChanged) {
      this.selectedSubDepartmentByStore[storeId] = null;
    }
    this.afterPerStoreSelection('department', storeId);
    if (departmentChanged) {
      // The store's sub-department no longer belongs to its department, so mirror the cleared
      // value back to the form. Without this the form still reads as complete and Add Item stays
      // enabled while the user has no valid sub-department for that store.
      this.mirrorPrimaryPerStoreToForm('subDepartment');
    }
    this.invalidateGeneratedCodes();
  }

  public selectPerStoreSubDepartment(storeId: string, subDepartment: ISubDepartment): void {
    this.selectedSubDepartmentByStore[storeId] = subDepartment;
    this.afterPerStoreSelection('subDepartment', storeId);
    this.invalidateGeneratedCodes();
  }

  public selectPerStoreVatRate(storeId: string, option: IVatRateOption): void {
    const primaryId = this.selectedStoresList[0]?.storeId;
    const previousControllingRate = primaryId ? this.selectedVatRateByStore[primaryId]?.rate ?? null : null;

    this.selectedVatRateByStore[storeId] = option;

    if (storeId === primaryId && option?.rate !== previousControllingRate) {
      // The controlling percentage changed: snap every other store to the new percentage so all
      // stores stay on the same rate (any previous different-% selections are no longer valid).
      this.reMatchVatRatesToControllingStore();
    } else if (storeId !== primaryId) {
      // Filling a non-controlling store still propagates to any stores that are still blank.
      this.applyAutoMatch('vatRate', storeId);
    }

    this.openPerStoreDropdownStoreId = null;
    this.perStoreSearchTerm[storeId] = '';
    this.mirrorPrimaryPerStoreToForm('vatRate');
  }

  private reMatchVatRatesToControllingStore(): void {
    const primaryId = this.selectedStoresList[0]?.storeId;
    const source = primaryId ? this.selectedVatRateByStore[primaryId] : null;
    for (const store of this.selectedStoresList) {
      if (store.storeId === primaryId) continue;
      this.selectedVatRateByStore[store.storeId] = source
        ? this.matchVatRate(source, this.vatRatesByStore[store.storeId] || [])
        : null;
    }
  }

  /** Whether a supplier code is part of a given store's additional-suppliers selection. */
  public isPerStoreMultiSupplierSelected(storeId: string, key: string): boolean {
    return (this.selectedMultiSuppliersByStore[storeId] || []).some((o: ISupplierOption) => o.key === key);
  }

  /**
   * Toggle an additional supplier for a store. Each store's additional-supplier list is fully
   * independent — no auto-match to other stores and no link to the regular supplier — so every
   * store can pick any suppliers it wants. The dropdown stays open (multi-select).
   */
  public togglePerStoreMultiSupplier(storeId: string, option: ISupplierOption): void {
    const current = this.selectedMultiSuppliersByStore[storeId] || [];
    const exists = current.some((o: ISupplierOption) => o.key === option.key);
    this.selectedMultiSuppliersByStore[storeId] = exists
      ? current.filter((o: ISupplierOption) => o.key !== option.key)
      : [...current, option];
  }

  private afterPerStoreSelection(field: PerStoreField, storeId: string): void {
    this.openPerStoreDropdownStoreId = null;
    this.perStoreSearchTerm[storeId] = '';
    this.applyAutoMatch(field, storeId);
    this.mirrorPrimaryPerStoreToForm(field);
  }

  /** Display text for a store's current selection (used in the modal rows). */
  public getPerStoreSelectionDisplay(storeId: string): string {
    switch (this.activePerStoreField) {
      case 'supplier': {
        const o = this.selectedSupplierByStore[storeId];
        if (!o) return '';
        return o.description ? `${o.key} - ${o.description}` : o.key;
      }
      case 'lineColour':
        return this.selectedLineColourByStore[storeId]?.name || '';
      case 'vatRate':
        return this.selectedVatRateByStore[storeId]?.description || '';
      case 'multiSupplier':
        return (this.selectedMultiSuppliersByStore[storeId] || []).map((o: ISupplierOption) => o.key).join(', ');
      case 'department':
        return this.selectedDepartmentByStore[storeId]?.name || '';
      case 'subDepartment':
        return this.selectedSubDepartmentByStore[storeId]?.name || '';
      default: {
        const _exhaustive: never = this.activePerStoreField;
        return _exhaustive;
      }
    }
  }

  public getPerStoreLineColourHex(storeId: string): string {
    return this.selectedLineColourByStore[storeId]?.color || '#FFFFFF';
  }

  //#endregion

  //#region --> Auto-match across stores
  /**
   * After a value is chosen for `sourceStoreId`, fill every other selected store that has no
   * explicit selection with the closest match (same code first, then name; otherwise blank).
   */
  private applyAutoMatch(field: PerStoreField, sourceStoreId: string): void {
    for (const store of this.selectedStoresList) {
      const targetId = store.storeId;
      if (targetId === sourceStoreId) continue;
      // Don't overwrite a store the user (or a previous match) already gave a concrete value.
      if (this.getSelectionForField(field, targetId)) continue;

      if (field === 'supplier') {
        const source = this.selectedSupplierByStore[sourceStoreId];
        this.selectedSupplierByStore[targetId] = source
          ? this.matchSupplier(source, this.suppliersByStore[targetId] || [])
          : null;
      } else if (field === 'lineColour') {
        const source = this.selectedLineColourByStore[sourceStoreId];
        this.selectedLineColourByStore[targetId] = source
          ? this.matchLineColour(source, this.lineColoursByStore[targetId] || [])
          : null;
      } else if (field === 'vatRate') {
        const source = this.selectedVatRateByStore[sourceStoreId];
        this.selectedVatRateByStore[targetId] = source
          ? this.matchVatRate(source, this.vatRatesByStore[targetId] || [])
          : null;
      } else if (field === 'department') {
        const source = this.selectedDepartmentByStore[sourceStoreId];
        this.selectedDepartmentByStore[targetId] = source
          ? this.matchDepartment(source, this.departmentsByStore[targetId] || [])
          : null;
        if (!this.selectedDepartmentByStore[targetId]) {
          this.selectedSubDepartmentByStore[targetId] = null;
        }
      } else if (field === 'subDepartment') {
        const source = this.selectedSubDepartmentByStore[sourceStoreId];
        const targetDepartment = this.selectedDepartmentByStore[targetId];
        if (!source || !targetDepartment) {
          this.selectedSubDepartmentByStore[targetId] = null;
        } else {
          const subDepartments = this.getSubDepartmentsForStoreDepartment(
            targetId,
            targetDepartment.department.toString(),
          );
          this.selectedSubDepartmentByStore[targetId] = this.matchSubDepartment(source, subDepartments);
        }
      }
      // 'multiSupplier' is intentionally excluded: each store's additional suppliers are independent.
    }
  }

  private matchSupplier(source: ISupplierOption, options: ISupplierOption[]): ISupplierOption | null {
    if (!source || options.length === 0) return null;
    const byCode = options.find((o: ISupplierOption) => o.key === source.key);
    if (byCode) return byCode;
    const target = (source.description || '').trim().toLowerCase();
    if (!target) return null;
    return options.find((o: ISupplierOption) => (o.description || '').trim().toLowerCase() === target)
      ?? options.find((o: ISupplierOption) => (o.description || '').trim().toLowerCase().startsWith(target))
      ?? options.find((o: ISupplierOption) => (o.description || '').trim().toLowerCase().includes(target))
      ?? null;
  }

  private matchLineColour(source: ILineColorOption, options: ILineColorOption[]): ILineColorOption | null {
    if (!source || options.length === 0) return null;
    const sourceCode = this.lineColourCode(source);
    const byCode = options.find((o: ILineColorOption) => this.lineColourCode(o) === sourceCode);
    if (byCode) return byCode;
    const target = this.lineColourDescription(source);
    if (!target) return null;
    return options.find((o: ILineColorOption) => this.lineColourDescription(o) === target)
      ?? options.find((o: ILineColorOption) => this.lineColourDescription(o).includes(target))
      ?? null;
  }

  private matchDepartment(source: IDepartment, options: IDepartment[]): IDepartment | null {
    if (!source || options.length === 0) return null;
    const byCode = options.find((o: IDepartment) => o.department.toString() === source.department.toString());
    if (byCode) return byCode;
    const target = (source.name || '').trim().toLowerCase();
    if (!target) return null;
    return options.find((o: IDepartment) => (o.name || '').trim().toLowerCase() === target)
      ?? options.find((o: IDepartment) => (o.name || '').trim().toLowerCase().startsWith(target))
      ?? options.find((o: IDepartment) => (o.name || '').trim().toLowerCase().includes(target))
      ?? null;
  }

  private matchSubDepartment(source: ISubDepartment, options: ISubDepartment[]): ISubDepartment | null {
    if (!source || options.length === 0) return null;
    const byCode = options.find((o: ISubDepartment) => o.subDepartment.toString() === source.subDepartment.toString());
    if (byCode) return byCode;
    const target = (source.name || '').trim().toLowerCase();
    if (!target) return null;
    return options.find((o: ISubDepartment) => (o.name || '').trim().toLowerCase() === target)
      ?? options.find((o: ISubDepartment) => (o.name || '').trim().toLowerCase().startsWith(target))
      ?? options.find((o: ISubDepartment) => (o.name || '').trim().toLowerCase().includes(target))
      ?? null;
  }

  /**
   * Match a VAT rate across stores. All stores must use the SAME percentage, so we only ever
   * consider options whose rate equals the source's rate; within those we prefer the same id,
   * then the same description, otherwise the first option at that percentage. If a store has no
   * option at that percentage the result is blank.
   */
  private matchVatRate(source: IVatRateOption, options: IVatRateOption[]): IVatRateOption | null {
    if (!source || options.length === 0) return null;
    const sameRate = options.filter((o: IVatRateOption) => o.rate === source.rate);
    if (sameRate.length === 0) return null;
    const target = (source.description || '').trim().toLowerCase();
    return sameRate.find((o: IVatRateOption) => o.id === source.id)
      ?? sameRate.find((o: IVatRateOption) => (o.description || '').trim().toLowerCase() === target)
      ?? sameRate[0];
  }

  private lineColourCode(option: ILineColorOption): string {
    return (option?.name || '').split(' - ')[0].trim();
  }

  private lineColourDescription(option: ILineColorOption): string {
    const parts = (option?.name || '').split(' - ');
    return parts.slice(1).join(' - ').trim().toLowerCase();
  }

  /** Mirror the primary store's per-store selection into the form so validation & base payload stay correct. */
  private mirrorPrimaryPerStoreToForm(field: PerStoreField): void {
    const primaryId = this.selectedStoresList[0]?.storeId;
    if (!primaryId) return;

    const silentUpdate = {emitEvent: false};

    switch (field) {
      case 'supplier':
        this.form.get('supplier.regularSuppCode')?.setValue(this.selectedSupplierByStore[primaryId]?.key ?? '', silentUpdate);
        this.form.get('supplier.regularSuppCode')?.markAsDirty();
        break;
      case 'lineColour':
        this.form.get('basic.lineColour')?.setValue(this.selectedLineColourByStore[primaryId]?.name ?? '', silentUpdate);
        this.form.get('basic.lineColour')?.markAsDirty();
        break;
      case 'vatRate':
        this.form.get('pricing.vatRate')?.setValue(this.selectedVatRateByStore[primaryId] ?? null, silentUpdate);
        this.form.get('pricing.vatRate')?.markAsDirty();
        // The silent update skips the vatRate subscription, so the price fields that depend on a
        // VAT rate being present must be re-evaluated here.
        this.updatePricingFieldsDisabledState();
        break;
      case 'department':
        this.form.get('basic.dep')?.setValue(this.selectedDepartmentByStore[primaryId]?.department.toString() ?? '', silentUpdate);
        this.form.get('basic.dep')?.markAsDirty();
        break;
      case 'subDepartment':
        this.form.get('basic.subDep')?.setValue(
          this.selectedSubDepartmentByStore[primaryId]?.subDepartment.toString() ?? '',
          silentUpdate,
        );
        this.form.get('basic.subDep')?.markAsDirty();
        break;
      // 'multiSupplier' is intentionally not mirrored to the form: each store's additional
      // suppliers are independent and saved straight from selectedMultiSuppliersByStore.
    }
  }

  //#endregion
  //#endregion

  public onEnterKeyNavigation(event: Event, currentSubGroup: string, currentField: string): void {
    event.preventDefault();
    this.selectBestMatch(event, currentField);

    const currentFieldOrder = this.fieldNavigationOrder[currentSubGroup];
    const currentFieldIndex = currentFieldOrder.indexOf(currentField);

    if (currentFieldIndex !== -1 && currentFieldIndex < currentFieldOrder.length - 1) {
      const nextField = currentFieldOrder[currentFieldIndex + 1];
      this.focusField(currentSubGroup, nextField);
      return;
    }

    const currentSubGroupIndex = this.subGroupOrder.indexOf(currentSubGroup);
    if (currentSubGroupIndex !== -1 && currentSubGroupIndex < this.subGroupOrder.length - 1) {
      const nextSubGroup = this.subGroupOrder[currentSubGroupIndex + 1];
      const firstFieldInNextSubGroup = this.fieldNavigationOrder[nextSubGroup][0];
      this.focusField(nextSubGroup, firstFieldInNextSubGroup);
    }
  }

  //#endregion

  //#region Price Calculation Methods

  //#region Form Submission and modal handling

  private applyPerStoreFieldOverride(
    target: Record<string, StockItemUpdateFieldValue>,
    key: string,
    value: StockItemUpdateFieldValue,
  ): void {
    if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete target[key];
    } else {
      target[key] = value;
    }
  }

  /**
   * Ensure per-store selections exist for submit. For fields the user never opened, populate
   * every store from the primary store's value (auto-matched). Fields the user did engage with
   * keep their selections (including intentional blanks).
   */
  private async ensurePerStoreSelectionsForSubmit(): Promise<void> {
    // Department trees are required for sub-department seeding and auto-match even when the user
    // already picked a department (that skip only applies to seed/match, not option data).
    await this.ensurePerStoreOptionsLoaded('department');

    const fields: PerStoreField[] = ['department', 'subDepartment', 'supplier', 'lineColour', 'vatRate'];
    for (const field of fields) {
      if (this.hasAnyPerStoreSelection(field)) {
        continue;
      }
      if (field !== 'department') {
        await this.ensurePerStoreOptionsLoaded(field);
      }
      this.seedPerStoreSelectionsFromForm(field);
      const primaryId = this.selectedStoresList[0]?.storeId;
      if (primaryId && this.getSelectionForField(field, primaryId)) {
        this.applyAutoMatch(field, primaryId);
      }
    }
  }

  /** Build the STOCK_UPDATE document for a new item at the given stock code. */
  private buildNewStockItemUpdateObj(
    changes: Record<string, StockItemUpdateFieldValue>,
    stockCode: string,
    description: string,
  ): StockItemUpdateObj {
    const payloadChanges: Record<string, StockItemUpdateFieldValue> = {...changes, code: stockCode};
    const original: Record<string, StockItemUpdateFieldValue> = {};
    for (const key of Object.keys(payloadChanges)) {
      original[key] = '' as const;
    }
    return {
      o: original,
      n: payloadChanges,
      details: {['1']: description},
    };
  }

  public getInvalidFieldsMessage(): string {
    if (this.form && this.form.invalid) {
      const invalidFields: string[] = [];

      const basicGroup = this.form.get('basic');
      if (basicGroup && basicGroup.invalid) {
        if (basicGroup.get('dep')?.invalid) {
          const errors = basicGroup.get('dep')?.errors;
          invalidFields.push(errors?.required ? 'Department (required)' : 'Department');
        }
        if (basicGroup.get('subDep')?.invalid) {
          const errors = basicGroup.get('subDep')?.errors;
          invalidFields.push(errors?.required ? 'Sub-Department (required)' : 'Sub-Department');
        }
        if (basicGroup.get('code')?.invalid) {
          const errors = basicGroup.get('code')?.errors;
          invalidFields.push(errors?.required ? 'Code (required)' : 'Code');
        }
        if (basicGroup.get('desc')?.invalid) {
          const errors = basicGroup.get('desc')?.errors;
          if (errors?.required) {
            invalidFields.push('Description (required)');
          } else if (errors?.maxlength) {
            invalidFields.push(`Description (exceeds ${errors.maxlength.requiredLength} characters)`);
          } else {
            invalidFields.push('Description');
          }
        }
        if (basicGroup.get('alternateDescription')?.invalid) {
          const errors = basicGroup.get('alternateDescription')?.errors;
          if (errors?.maxlength) {
            invalidFields.push(`Alternative Description (exceeds ${errors.maxlength.requiredLength} characters)`);
          } else {
            invalidFields.push('Alternative Description');
          }
        }
      }

      const pricingGroup = this.form.get('pricing');
      if (pricingGroup && pricingGroup.invalid) {
        if (pricingGroup.get('latestCost')?.invalid) {
          const errors = pricingGroup.get('latestCost')?.errors;
          if (errors?.required) {
            invalidFields.push('Latest Cost (required)');
          } else if (errors?.min) {
            invalidFields.push('Latest Cost (must be greater than 0)');
          } else {
            invalidFields.push('Latest Cost');
          }
        }
        if (pricingGroup.get('vatRate')?.invalid) {
          const errors = pricingGroup.get('vatRate')?.errors;
          invalidFields.push(errors?.required ? 'VAT Rate (required)' : 'VAT Rate');
        }
        if (pricingGroup.get('sellPriIncl1')?.invalid) {
          const errors = pricingGroup.get('sellPriIncl1')?.errors;
          if (errors?.required) {
            invalidFields.push('Selling Price (incl) (required)');
          } else if (errors?.min) {
            invalidFields.push('Selling Price (incl) (must be greater than 0)');
          } else {
            invalidFields.push('Selling Price (incl)');
          }
        }
        if (pricingGroup.get('sellPriExcl1')?.invalid) {
          const errors = pricingGroup.get('sellPriExcl1')?.errors;
          if (errors?.required) {
            invalidFields.push('Selling Price (excl) (required)');
          } else if (errors?.min) {
            invalidFields.push('Selling Price (excl) (must be greater than 0)');
          } else {
            invalidFields.push('Selling Price (excl)');
          }
        }
      }

      const supplierGroup = this.form.get('supplier');
      if (supplierGroup && supplierGroup.invalid) {
        if (supplierGroup.get('regularSuppCode')?.invalid) {
          const errors = supplierGroup.get('regularSuppCode')?.errors;
          invalidFields.push(errors?.required ? 'Regular Supplier (required)' : 'Regular Supplier');
        }
        if (supplierGroup.get('suppCode')?.invalid) {
          const errors = supplierGroup.get('suppCode')?.errors;
          invalidFields.push(errors?.required ? 'Supplier Stock Code (required)' : 'Supplier Stock Code');
        }
      }

      if (invalidFields.length > 0) {
        return `Errors: ${invalidFields.join(', ')}`;
      }
    }

    return '';
  }

  /**
   * A manually entered code is shared by every selected store; generated codes are per store.
   */
  private resolveStockCodeForStore(storeId: string, formCode: string | undefined): string {
    if (this.isManualCodeEntry) {
      return this.manualStockCode;
    }
    if (this.isMultiStore) {
      return this.generatedCodesByStore[storeId] ?? '';
    }
    return formCode?.trim() || this.generatedCodesByStore[storeId] || '';
  }

  /**
   * Stores whose VAT rate would be dropped from the payload. The form's required validator only
   * covers the primary store, so a multi-store add can reach submit with stores that auto-matched
   * to no VAT rate — the backend then rejects the item with "New item VAT rate not provided".
   */
  private getStoresMissingVatRate(): IStore[] {
    const formVatRateId = (this.form.get('pricing.vatRate')?.value as IVatRateOption | null)?.id;
    return this.selectedStoresList.filter((store: IStore) => {
      const vatRateId = this.isMultiStore ? this.selectedVatRateByStore[store.storeId]?.id : formVatRateId;
      return parseVatRateId(vatRateId) === '';
    });
  }

  /**
   * Stores with no department or no sub-department selected. Changing a store's department clears
   * its sub-department, so a multi-store add can otherwise reach submit with an incomplete store.
   */
  private getStoresMissingDepartments(): IStore[] {
    if (!this.isMultiStore) {
      return [];
    }
    return this.selectedStoresList.filter((store: IStore) =>
      !this.selectedDepartmentByStore[store.storeId] || !this.selectedSubDepartmentByStore[store.storeId],
    );
  }

  private async showMissingDepartmentError(missing: IStore[]): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Validation Error',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Department or sub-department missing for some stores',
      contextDescription: [
        'The following store(s) still need a department and sub-department:',
        Ascii.EmptySpace,
        ...missing.map((store: IStore) => {
          const missingFields = [
            this.selectedDepartmentByStore[store.storeId] ? '' : 'department',
            this.selectedSubDepartmentByStore[store.storeId] ? '' : 'sub-department',
          ].filter(Boolean).join(' and ');
          return `• ${store.name} — missing ${missingFields}`;
        }),
        Ascii.EmptySpace,
        'Open Select Departments and Select Sub-Departments to choose a value for each store.',
      ],
    };
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private async showMissingVatRateError(missing: IStore[]): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Validation Error',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'VAT rate missing for some stores',
      contextDescription: [
        'A stock item cannot be created without a VAT rate. The following store(s) have no VAT rate selected:',
        Ascii.EmptySpace,
        ...missing.map((store: IStore) => `• ${store.name}`),
        Ascii.EmptySpace,
        this.isMultiStore
          ? 'Open Select VAT Rates and choose a rate for each store, or remove those stores from the selection.'
          : 'Please select a VAT rate before adding the item.',
      ],
    };
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private async showStockCodeNotCheckedError(): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Validation Error',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Stock code not checked',
      contextDescription: [
        'Please click Check Stock Code to confirm the code is not already used in the selected store(s) before adding the item.',
      ],
    };
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private async showStockCodesMissingError(): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: 'OK',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Validation Error',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Stock codes missing',
      contextDescription: ['Please generate stock codes before adding the item.'],
    };
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private buildStockItemFromForm(formData: ReturnType<FormGroup['getRawValue']>): StockItem {
    const barcodesArray = this.selectedBarcodeKeys;
    const primaryBarcode = barcodesArray.length > 0 ? barcodesArray[0] : '';
    const multipleBarcodes = barcodesArray.length > 1 ? barcodesArray.slice(1) : [];

    return {
      alternateDescription: (formData.basic.alternateDescription as string)?.trim() ?? '',
      barcode: primaryBarcode,
      multipleBarcodes,
      code: formData.basic.code,
      dep: formData.basic.dep,
      desc: formData.basic.desc,
      lineColourCode: (formData.basic.lineColour as string).split(' - ')[0].trim(),
      maxOrderLevel: formData.ordering.maxOrderLevel || 0,
      minOrderLevel: formData.ordering.minOrderLevel || 0,
      multipleSuppliers: formData.supplier.supplierCodes || [],
      onHoldCode: formData.basic.onHoldCode ? encodeOnHoldCode(this.selectedOnHoldValues) : 0,
      ordLvl: formData.ordering.ordLvl || 0,
      packSize: formData.ordering.packSize || 1,
      recommendedGP: formData.pricing.recommendedGP,
      regularSuppCode: formData.supplier.regularSuppCode,
      sellPriExcl1: formData.pricing.sellPriExcl1,
      sellPriIncl1: formData.pricing.sellPriIncl1,
      subDep: formData.basic.subDep,
      suppCode: formData.supplier.suppCode,
      latestCost: formData.pricing.latestCost,
      vatR: (parseVatRateId((formData.pricing.vatRate as IVatRateOption)?.id)) as number,
      _tags: this.selectedTags,
    } as unknown as StockItem;
  }

  private buildChangesFromStockItem(stockItem: StockItem): Record<string, StockItemUpdateFieldValue> {
    const changes: Record<string, StockItemUpdateFieldValue> = {};
    const stockItemRecord = stockItem as unknown as Record<string, StockItemUpdateFieldValue>;
    for (const key of Object.keys(stockItem)) {
      const value = stockItemRecord[key];

      // The firebase service rejects fields whose new value equals the original (which we set to '').
      // Skip blank fields (empty string or empty array) so they aren't flagged as "update == original".
      if (value === '' || value === null || value === undefined) continue;
      if (Array.isArray(value) && value.length === 0) continue;

      changes[key] = value;
    }
    return changes;
  }

  public async onSubmit(event: Event): Promise<void> {
    event.preventDefault();
    if (!this.form.valid || this.isSubmitting) return;

    this.isSubmitting = true;
    try {
      await this.submitStockItem();
    } finally {
      this.isSubmitting = false;
    }
  }

  private async submitStockItem(): Promise<void> {
    if (this.selectedStoresList.length === 0) {
      this.isSubmitting = false;
      const componentProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: 'OK',
        buttonClose: true,
        buttonCloseCross: true,
        buttonCloseText: 'Close',
        modalTitle: 'Validation Error',
        modalTitleIcon: IconsEnum.alertBox,
        modalTitleIconColor: IconColourEnum.red,
        contextHeading: 'Missing Required Field',
        contextDescription: ['Please select at least one store to add this stock item to.'],
      };
      const modalController = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps,
        cssClass: ['shared-basic-modal-css-medium'],
        backdropDismiss: true,
      });
      await modalController.present();
      return;
    }

    // Validate that at least one barcode is provided
    if (!this.selectedBarcodeKeys || this.selectedBarcodeKeys.length === 0) {
      this.isSubmitting = false;
      const componentProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: 'OK',
        buttonClose: true,
        buttonCloseCross: true,
        buttonCloseText: 'Close',
        modalTitle: 'Validation Error',
        modalTitleIcon: IconsEnum.alertBox,
        modalTitleIconColor: IconColourEnum.red,
        contextHeading: 'Missing Required Field',
        contextDescription: ['At least one barcode is required to create a stock item.'],
      };
      const modalController = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps,
        cssClass: ['shared-basic-modal-css-medium'],
        backdropDismiss: true,
      });
      await modalController.present();
      return;
    }

    // Authoritative duplicate check against Firestore for every selected store. Runs even when the
    // user already pressed Check Barcodes, unless that result still matches the current list.
    if (!this.areBarcodesVerified() && !(await this.onCheckBarcodesAvailability())) {
      this.isSubmitting = false;
      if (this.barcodeConflicts.length > 0) {
        await this.showBarcodeStoreConflictError(this.barcodeConflicts);
      }
      return;
    }

    if (this.isManualCodeEntry && !this.isManualStockCodeVerified()) {
      this.isSubmitting = false;
      await this.showStockCodeNotCheckedError();
      return;
    }

    if (this.isMultiStore) {
      if (!this.hasGeneratedCodesForAllSelectedStores()) {
        this.isSubmitting = false;
        await this.showStockCodesMissingError();
        return;
      }
      await this.ensurePerStoreSelectionsForSubmit();
    }

    const storesMissingDepartments = this.getStoresMissingDepartments();
    if (storesMissingDepartments.length > 0) {
      this.isSubmitting = false;
      await this.showMissingDepartmentError(storesMissingDepartments);
      return;
    }

    const storesMissingVatRate = this.getStoresMissingVatRate();
    if (storesMissingVatRate.length > 0) {
      this.isSubmitting = false;
      await this.showMissingVatRateError(storesMissingVatRate);
      return;
    }

    const formData = this.form.getRawValue();
    const stockItem = this.buildStockItemFromForm(formData);

    if (!this.isMultiStore) {
      const stockId = stockItem.code?.trim();
      if (!stockId) {
        return;
      }
    } else if (!this.hasGeneratedCodesForAllSelectedStores()) {
      this.isSubmitting = false;
      await this.showStockCodesMissingError();
      return;
    }

    const changes = this.buildChangesFromStockItem(stockItem);
    const itemsByStore: Record<string, Record<string, StockItemUpdateObj>> = {};
    for (const store of this.selectedStoresList) {
      const code = this.resolveStockCodeForStore(store.storeId, stockItem.code);
      if (!code) {
        continue;
      }

      const perStoreChanges: Record<string, StockItemUpdateFieldValue> = {...changes};
      if (this.isMultiStore) {
        this.applyPerStoreFieldOverride(
          perStoreChanges,
          'dep',
          this.selectedDepartmentByStore[store.storeId]?.department.toString() ?? '',
        );
        this.applyPerStoreFieldOverride(
          perStoreChanges,
          'subDep',
          this.selectedSubDepartmentByStore[store.storeId]?.subDepartment.toString() ?? '',
        );
        this.applyPerStoreFieldOverride(
          perStoreChanges,
          'regularSuppCode',
          this.selectedSupplierByStore[store.storeId]?.key ?? '',
        );
        const lineColour = this.selectedLineColourByStore[store.storeId];
        this.applyPerStoreFieldOverride(
          perStoreChanges,
          'lineColourCode',
          lineColour ? this.lineColourCode(lineColour) : '',
        );
        this.applyPerStoreFieldOverride(
          perStoreChanges,
          'vatR',
          parseVatRateId(this.selectedVatRateByStore[store.storeId]?.id),
        );
        const multiSupplierCodes = (this.selectedMultiSuppliersByStore[store.storeId] || []).map(
          (o: ISupplierOption) => o.key,
        );
        this.applyPerStoreFieldOverride(perStoreChanges, 'multipleSuppliers', multiSupplierCodes);
      }

      itemsByStore[store.storeId] = {
        [code]: this.buildNewStockItemUpdateObj(perStoreChanges, code, stockItem.desc ?? ''),
      };
    }

    if (Object.keys(itemsByStore).length === 0) {
      this.isSubmitting = false;
      await this.showStockCodesMissingError();
      return;
    }

    const primaryStoreId = this.selectedStoresList[0]?.storeId;
    const primaryCode = Object.keys(itemsByStore[primaryStoreId] ?? {})[0] ?? '';
    const primaryPayload = itemsByStore[primaryStoreId]?.[primaryCode];

    this.store.dispatch(createNewStockItem({
      newStockItem: primaryCode && primaryPayload ? {[primaryCode]: primaryPayload} : {},
      ...(this.isMultiStore
        ? {
          storeIds: this.selectedStoresList.map((s: IStore) => s.storeId),
          itemsByStore,
        }
        : {}),
    }));

    const outcome = await firstValueFrom(
      this.store.select(selectCreateNewStockItemSubmit).pipe(
        filter((submit) => submit.status === 'success' || submit.status === 'failure'),
        take(1),
      ),
    );

    if (outcome.status === 'success') {
      if (this.retryAddStockItem && this.retryNotification && !this.retryNotification.retried) {
        this.store.dispatch(updateNotification({
          notification: {
            ...this.retryNotification,
            retried: true,
          },
        }));
      }
      await this.onModalClose(true);
      return;
    }

    await this.handleCreateNewStockItemFailure(outcome);
  }

  private async handleCreateNewStockItemFailure(outcome: {
    failed: { storeId: string; message?: string }[];
    succeededStoreIds: string[];
  }): Promise<void> {
    const failedStoreIds = new Set(outcome.failed.map((f) => f.storeId));
    const manualCode = this.isManualCodeEntry ? this.manualStockCode : '';

    // Only keep failed stores selected so the next Add Item retries those stores only.
    this.selectedStoresList = this.selectedStoresList.filter((s: IStore) => failedStoreIds.has(s.storeId));

    const nextCodes: Record<string, string> = {};
    failedStoreIds.forEach((storeId: string) => {
      if (this.generatedCodesByStore[storeId]) {
        nextCodes[storeId] = this.generatedCodesByStore[storeId];
      }
    });
    this.generatedCodesByStore = nextCodes;

    if (!this.isManualCodeEntry) {
      const primaryCode = this.generatedCodesByStore[this.selectedStoresList[0]?.storeId] ?? '';
      this.form.get('basic.code')?.setValue(primaryCode);
    }

    const lines: string[] = [];
    if (outcome.succeededStoreIds.length > 0) {
      lines.push(
        'Added successfully to:',
        ...outcome.succeededStoreIds.map((storeId: string) => `• ${this.getStoreNameById(storeId)}`),
        '',
      );
    }
    lines.push(
      'Failed to add to:',
      ...outcome.failed.map((f) => {
        const name = this.getStoreNameById(f.storeId);
        const code = manualCode || this.generatedCodesByStore[f.storeId] || '';
        const detail = f.message ? ` (${f.message})` : '';
        return `• ${code} — ${name}${detail}`;
      }),
      '',
      'Only the failed store(s) remain selected. Click Add Item to retry those stores.',
    );

    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: true,
      buttonCloseText: 'Close',
      modalTitle: 'Could Not Add to All Stores',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: outcome.succeededStoreIds.length > 0
        ? 'The item was added to some stores but not all.'
        : 'The item could not be added.',
      contextDescription: lines,
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
      backdropDismiss: true,
    });
    await modal.present();
  }

  private getStoreNameById(storeId: string): string {
    return this.stores.find((s: IStore) => s.storeId === storeId)?.name
      ?? this.selectedStoresList.find((s: IStore) => s.storeId === storeId)?.name
      ?? storeId;
  }

  public async onModalClose(skipUnsavedCheck: boolean = false): Promise<void> {
    // Check if form has any changes
    const hasChanges = this.form.dirty ||
      this.selectedBarcodeKeys.length > 0 ||
      this.selectedTags.length > 0 ||
      this.selectedSuppliers.length > 0 ||
      this.selectedStoresList.length > 1;

    if (!skipUnsavedCheck && hasChanges) {
      const componentProps: ISharedModalBasic = {
        buttonAccept: true,
        buttonAcceptText: 'Discard Changes & Continue',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Cancel',
        modalTitle: 'Unsaved Changes',
        modalTitleIcon: IconsEnum.infoSlabCircleOutline,
        modalTitleIconColor: IconColourEnum.yellow,
        contextHeading: 'If you close this form, your changes will be lost.',
        contextDescription: ['Are you sure you want to continue?'],
      };

      const confirmModal = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps,
        cssClass: ['shared-basic-modal-css-medium'],
        backdropDismiss: false,
      });

      await confirmModal.present();
      const {role} = await confirmModal.onDidDismiss();

      if (role !== 'modal-accept') {
        return;
      }
    }

    this.destroy$.next(true);
    this.destroy$.complete();
    this.store.dispatch(resetCreateNewStockItemSubmit());
    void this.modalController.dismiss();
  }

  //#region Form Navigation Methods

  private populateFormFromStockData(stockData: Record<string, StockItemUpdateFieldValue>): void {

    Object.entries(stockData).forEach(([key, value]) => {
      // Try to map integer key to field name, or use the key directly if it's already a field name
      let fieldName: string = sItemIntToKey[key];

      // If no mapping found, the key might already be a field name (like 'code', 'desc', etc.)
      if (!fieldName) {
        fieldName = key;
      }

      if (!fieldName || value === null || value === undefined) {
        console.warn(`[Add Stock Item] Skipping field "${key}" - fieldName: ${fieldName}, value: ${value}`);
        return;
      }

      this.setFormValueByFieldName(fieldName as keyof StockItem, value);
    });

    if (stockData.multipleSuppliers && Array.isArray(stockData.multipleSuppliers)) {
      this.selectedSuppliers = [...stockData.multipleSuppliers];
      this.form.get('supplier.supplierCodes')?.setValue(this.selectedSuppliers);
    }

    if (stockData._tags && Array.isArray(stockData._tags)) {
      this.selectedTags = [...stockData._tags];

      // Update tagsOptions to mark these tags as selected
      Object.keys(this.tagsOptions).forEach((tagName: string) => {
        this.tagsOptions[tagName].selected = this.selectedTags.includes(tagName);
      });
    }

    // Clear the stock code so user must generate a new one (avoid duplicate code issues)
    this.form.get('basic.code')?.setValue('');
    this.form.get('basic.code')?.enable();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.updatePricingFieldsDisabledState();
  }

  //#endregion

  //#region --> Barcode Management
  /**
   * Converts barcode array to Record<string, ISelectOption> format for shared-custom-select
   */
  private convertBarcodesArrayToOptions(barcodes: string[]): void {
    this.barcodeOptions = {};
    barcodes.forEach((barcode: string, index: number) => {
      if (barcode && barcode.trim()) {
        const isPrimary = index === 0;
        this.barcodeOptions[barcode] = {
          name: isPrimary ? `${barcode} (Primary)` : barcode,
          selected: true,
          disabled: false,
        };
      }
    });
    this.selectedBarcodeKeys = barcodes.filter((b: string) => b && b.trim());
  }

  /**
   * Converts Record<string, ISelectOption> format back to barcode array
   */
  private convertBarcodeOptionsToArray(): string[] {
    return Object.keys(this.barcodeOptions).filter((key: string) => this.barcodeOptions[key].selected);
  }

  private focusField(_subGroup: string, fieldName: string): void {
    setTimeout((): void => {
      const fieldSelectors: Record<string, string> = {
        alternateDescription: 'input[formControlName="alternateDescription"]',
        barcode: 'input[formControlName="barcode"]',
        dep: 'input[data-field="department"]',
        desc: 'input[formControlName="desc"]',
        lineColour: 'input[data-field="lineColor"]',
        onHoldCode: 'input[data-field="onHoldCode"]',
        subDep: 'input[data-field="subDepartment"]',

        latestCost: 'input[formControlName="latestCost"]',
        nominalGP: 'input[formControlName="nominalGP"]',
        recommendedGP: 'input[formControlName="recommendedGP"]',
        sellPriExcl1: 'input[formControlName="sellPriExcl1"]',
        sellPriIncl1: 'input[formControlName="sellPriIncl1"]',
        vatRate: 'input[data-field="vatRate"]',

        maxOrderLevel: 'input[formControlName="maxOrderLevel"]',
        minOrderLevel: 'input[formControlName="minOrderLevel"]',
        ordLvl: 'input[formControlName="ordLvl"]',
        packSize: 'input[formControlName="packSize"]',

        regularSuppCode: 'input[data-field="supplier"]',
        suppCode: 'input[formControlName="suppCode"]',
        supplierCodes: 'input[data-field="multiSupplier"]',
      };

      const selector = fieldSelectors[fieldName];
      if (selector) {
        const element: HTMLElement = document.querySelector(selector);
        element?.focus();
      }
    }, 10);
  }

  //endregion
}
